import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialTransactionApi } from '../../services/api/financialTransactionApi';
import { financialAccountApi } from '../../services/api/financialAccountApi';
import { financialStatementApi } from '../../services/api/financialStatementApi';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../../contexts/AuthContext';
import { AccountAutosuggest } from '../../components/AccountAutosuggest';
import { useToast } from '@/hooks/use-toast';
import { Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  CreateTransactionDialog,
  UploadWireTransferDialog,
  ReleaseFundsDialog,
  useTransactionExpansion,
} from '@/components/financial-transactions';
import { downloadStatementPDF, downloadStatementCSV, downloadAllAccountsStatementPDF } from '@/utils/statementPdfUtils';
import { downloadAirwallexXLSX, getStateFullName, type AirwallexTransferRow } from '@/utils/airwallexExportUtils';
import type {
  FinancialTransaction,
  FinancialTransactionWithChildren,
  TransactionType,
  TransactionStatus,
} from '../../models/FinancialTransaction';
import type { FinancialStatement } from '../../models/FinancialStatement';

export const FinancialTransactions: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedRole } = useAuth();
  const [activeTab, setActiveTab] = useState<'transactions' | 'balances'>('transactions');
  const [selectedStatus, setSelectedStatus] = useState<TransactionStatus | ''>('');
  const [selectedType, setSelectedType] = useState<TransactionType | ''>('');
  const [selectedVerified, setSelectedVerified] = useState<'all' | 'yes' | 'no'>('all');
  const [discrepancyFilter, setDiscrepancyFilter] = useState<'all' | 'issues' | 'mismatches' | 'unmatched' | 'matched'>('all');
  const [selectedAssociation, setSelectedAssociation] = useState<string>('');
  const [selectedManagementCompany, setSelectedManagementCompany] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedTransaction, setSelectedTransaction] =
    useState<FinancialTransactionWithChildren | null>(null);
  const [observations, setObservations] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);

  // Use expansion hook
  const {
    expandedRows,
    childrenCache,
    loadingRows,
    toggleRowExpansion,
    setChildrenCache,
    cacheAllChildren,
  } = useTransactionExpansion();

  // Balance filters
  const [balanceSearchTerm, setBalanceSearchTerm] = useState('');
  const [balanceAccountTypeFilter, setBalanceAccountTypeFilter] = useState<string>('');
  const [balanceStatusFilter, setBalanceStatusFilter] = useState<string>('');
  const [balanceAmountFilter, setBalanceAmountFilter] = useState<'all' | 'positive' | 'negative' | 'zero'>('all');

  // Release funds state
  const [showReleaseFundsDialog, setShowReleaseFundsDialog] = useState(false);
  const [selectedAccountForRelease, setSelectedAccountForRelease] = useState<any>(null);
  const [releaseAmount, setReleaseAmount] = useState<string>('');
  const [selectedBatchId, setSelectedBatchId] = useState<number | 'new' | null>(null);
  const [newBatchName, setNewBatchName] = useState<string>('');

  // Upload wire transfer results state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [isPopulating, setIsPopulating] = useState(false);

  // Export/Import state
  const [isExporting, setIsExporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Statement export dialog state
  const [showStatementExportDialog, setShowStatementExportDialog] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<number | null>(null);
  const [statementExportFormat, setStatementExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [includeEmptyAccounts, setIncludeEmptyAccounts] = useState(true);
  const [includePlatform, setIncludePlatform] = useState(true);
  const [statementExportAccountId, setStatementExportAccountId] = useState<number | null>(null);
  const [isExportingStatement, setIsExportingStatement] = useState(false);

  // Withdrawal batches dialog state
  const [showBatchesDialog, setShowBatchesDialog] = useState(false);
  const [uploadingBatchId, setUploadingBatchId] = useState<number | null>(null);
  const [batchUploadFile, setBatchUploadFile] = useState<File | null>(null);
  const batchFileInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch all financial accounts for dropdowns (with balances)
  const { data: allAccounts } = useQuery({
    queryKey: ['financialAccounts'],
    queryFn: () => financialAccountApi.getAccounts({ include_balances: true }),
  });

  // Fetch statements for export dialog
  const { data: statements } = useQuery({
    queryKey: ['financialStatements'],
    queryFn: () => financialStatementApi.getStatements(),
  });

  // Fetch available batches for release funds dialog
  const { data: availableBatchesForRelease } = useQuery({
    queryKey: ['availableBatchesForRelease'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_batch_files')
        .select('*')
        .in('status', ['draft', 'generated'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: showReleaseFundsDialog,
  });

  // Fetch withdrawal batches
  const { data: withdrawalBatches, refetch: refetchBatches } = useQuery({
    queryKey: ['withdrawalBatches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_batch_files')
        .select(`
          *,
          financial_batch_payouts (
            id,
            payout_transaction_id,
            sequence_number,
            payout_reference,
            status
          )
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: showBatchesDialog,
  });

  // Fetch associations for management company users
  const { data: associationsForMC } = useQuery({
    queryKey: ['associations', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      if (selectedRole?.type !== 'management_company' || !selectedRole.entityId) {
        return [];
      }

      const { data, error } = await supabase
        .from('associations')
        .select('id')
        .eq('management_company_id', selectedRole.entityId);

      if (error) throw error;
      return data.map(a => a.id);
    },
    enabled: selectedRole?.type === 'management_company',
  });

  // Filter accounts based on user role
  const filteredAccounts = useMemo(() => {
    if (!allAccounts) return [];

    // Platform users see all accounts
    if (selectedRole?.type === 'platform') {
      return allAccounts;
    }

    // Management company users see platform + their associations
    if (selectedRole?.type === 'management_company') {
      return allAccounts.filter(account => {
        // Always show platform account
        if (account.account_type === 'platform') {
          return true;
        }

        // Show payment gateway (Stripe)
        if (account.account_type === 'payment_gateway') {
          return true;
        }

        // Show this management company's account
        if (account.account_type === 'management_company' &&
            account.reference === selectedRole.entityId?.toString()) {
          return true;
        }

        // Show associations belonging to this management company
        if (account.account_type === 'association' && associationsForMC) {
          const accountAssociationId = parseInt(account.reference || '0');
          return associationsForMC.includes(accountAssociationId);
        }

        return false;
      });
    }

    // Association users see platform + their own association
    if (selectedRole?.type === 'association') {
      return allAccounts.filter(account => {
        // Always show platform account
        if (account.account_type === 'platform') {
          return true;
        }

        // Show payment gateway (Stripe)
        if (account.account_type === 'payment_gateway') {
          return true;
        }

        // Show this association's account
        if (account.account_type === 'association' &&
            account.reference === selectedRole.entityId?.toString()) {
          return true;
        }

        return false;
      });
    }

    // Default: show all accounts
    return allAccounts;
  }, [allAccounts, selectedRole, associationsForMC]);

  // Filter accounts for balance view
  const filteredBalanceAccounts = useMemo(() => {
    if (!filteredAccounts) return [];

    // Exclude payment gateway accounts
    let filtered = filteredAccounts.filter(account => account.account_type !== 'payment_gateway');

    // Apply search filter
    if (balanceSearchTerm) {
      const searchLower = balanceSearchTerm.toLowerCase();
      filtered = filtered.filter(account => {
        const name = (account.display_name || account.reference || '').toLowerCase();
        const type = account.account_type.toLowerCase();
        return name.includes(searchLower) || type.includes(searchLower);
      });
    }

    // Apply account type filter
    if (balanceAccountTypeFilter) {
      filtered = filtered.filter(account => account.account_type === balanceAccountTypeFilter);
    }

    // Apply status filter
    if (balanceStatusFilter) {
      filtered = filtered.filter(account => account.status === balanceStatusFilter);
    }

    // Apply amount filter
    if (balanceAmountFilter !== 'all') {
      filtered = filtered.filter(account => {
        const currentBalance = account.balances?.find(b => b.balance_type === 'current');
        const amount = currentBalance?.amount || 0;

        if (balanceAmountFilter === 'positive') return amount > 0;
        if (balanceAmountFilter === 'negative') return amount < 0;
        if (balanceAmountFilter === 'zero') return amount === 0;
        return true;
      });
    }

    // Sort by available balance descending
    filtered.sort((a, b) => {
      const aBalance = a.balances?.find(b => b.balance_type === 'available')?.amount || 0;
      const bBalance = b.balances?.find(b => b.balance_type === 'available')?.amount || 0;
      return bBalance - aBalance; // Descending order
    });

    return filtered;
  }, [filteredAccounts, balanceSearchTerm, balanceAccountTypeFilter, balanceStatusFilter, balanceAmountFilter]);

  // Fetch all associations for filter dropdown
  const { data: associations } = useQuery({
    queryKey: ['associations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('associations')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch all management companies for filter dropdown
  const { data: managementCompanies } = useQuery({
    queryKey: ['managementCompanies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('management_companies')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch transactions (parents only for table display)
  const { data: transactionsResponse, isLoading, error } = useQuery({
    queryKey: ['financialTransactions', selectedStatus, selectedType, selectedAssociation, selectedManagementCompany, discrepancyFilter, currentPage, itemsPerPage],
    queryFn: () =>
      financialTransactionApi.getTransactions({
        status: selectedStatus || undefined,
        transaction_type: selectedType || undefined,
        parents_only: selectedType ? false : true, // When filtering by type, show actual transactions of that type
        association_id: selectedAssociation || undefined,
        management_company_id: selectedManagementCompany || undefined,
        discrepancy_filter: discrepancyFilter,
        page: currentPage,
        limit: itemsPerPage,
      }),
  });

  // Extract transactions and pagination info from response
  const allTransactions = transactionsResponse?.data || [];
  const totalTransactions = transactionsResponse?.total || 0;

  // Fetch reconciliation summary statistics from backend
  const { data: summaryStatsData } = useQuery({
    queryKey: ['reconciliationStats'],
    queryFn: () => financialTransactionApi.getReconciliationStats(),
  });

  // Build a map of parent IDs to their children for discrepancy checking
  const childrenByParentId = React.useMemo(() => {
    if (!allTransactions) return new Map();

    const map = new Map<number, FinancialTransaction[]>();
    allTransactions.forEach(txn => {
      if (txn.parent_transaction_id) {
        const children = map.get(txn.parent_transaction_id) || [];
        children.push(txn);
        map.set(txn.parent_transaction_id, children);
      }
    });
    return map;
  }, [allTransactions]);

  // Filter transactions based on verified status only (discrepancy filter is now server-side)
  const filteredTransactions = React.useMemo(() => {
    if (!allTransactions) return [];

    let filtered = allTransactions;

    // Apply verified filter (client-side since it's a simple filter)
    if (selectedVerified !== 'all') {
      filtered = filtered.filter(txn => {
        if (selectedVerified === 'yes') return txn.verified === true;
        if (selectedVerified === 'no') return !txn.verified;
        return true;
      });
    }

    // Discrepancy filter is now handled server-side
    return filtered;
  }, [allTransactions, selectedVerified]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, selectedType, discrepancyFilter, selectedVerified, selectedAssociation, selectedManagementCompany]);

  // Calculate pagination (use server-side total, but display client-filtered data)
  const totalPages = Math.ceil(totalTransactions / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  // For display, use the filtered transactions from the current page
  const transactions = filteredTransactions;

  // Pagination helpers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // DISABLED: Auto-expand causes N+1 query problem with 25+ API calls
  // Users can manually expand rows they want to see
  // React.useEffect(() => {
  //   console.log(`[Auto-expand Effect] Triggered. transactions:`, transactions?.length, 'expandedRows:', expandedRows.size);

  //   if (!transactions || transactions.length === 0) {
  //     console.log(`[Auto-expand Effect] No transactions, skipping`);
  //     return;
  //   }

  //   if (expandedRows.size > 0) {
  //     console.log(`[Auto-expand Effect] Already have expanded rows, skipping`);
  //     return;
  //   }

  //   console.log(`[Auto-expand Effect] Auto-expanding all parent transactions:`, transactions.map(t => t.id));

  //   // Auto-expand all parent transactions on initial load
  //   transactions.forEach(txn => {
  //     console.log(`[Auto-expand Effect] Calling toggleRowExpansion for transaction ${txn.id}`);
  //     toggleRowExpansion(txn.id);
  //   });
  // }, [transactions, toggleRowExpansion]); // Run when transactions change

  // Use summary statistics from backend (calculated from all transactions in database)
  const summaryStats = React.useMemo(() => {
    if (!summaryStatsData) return null;

    return {
      totalAmount: summaryStatsData.total_amount,
      totalExpected: summaryStatsData.total_expected,
      variance: summaryStatsData.variance,
      totalCount: summaryStatsData.total_count,
      discrepancyCount: summaryStatsData.discrepancy_count,
      unmatchedCount: summaryStatsData.unmatched_count,
      matchedCount: summaryStatsData.matched_count,
      orderPaymentCount: summaryStatsData.order_payment_count,
    };
  }, [summaryStatsData]);

  // Approve transaction mutation
  const approveMutation = useMutation({
    mutationFn: (transactionId: number) =>
      financialTransactionApi.approveTransaction(transactionId, {
        observations,
        approved_by: 1, // TODO: Get from auth context
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
      setSelectedTransaction(null);
      setObservations('');
    },
  });

  // Cancel transaction mutation
  const cancelMutation = useMutation({
    mutationFn: (transactionId: number) =>
      financialTransactionApi.cancelTransaction(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
      setSelectedTransaction(null);
    },
  });

  // Create transaction mutation
  const createMutation = useMutation({
    mutationFn: (data: any) =>
      financialTransactionApi.createTransaction(data),
    onSuccess: async (_, variables) => {
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });

      // If this was a child transaction, refresh the parent's children cache
      if (variables.parent_transaction_id) {
        try {
          const parentWithChildren = await financialTransactionApi.getTransaction(
            variables.parent_transaction_id,
            true,
            false
          );

          if (parentWithChildren.children) {
            setChildrenCache(prevCache => {
              const newCache = new Map(prevCache);
              cacheAllChildren(variables.parent_transaction_id, parentWithChildren.children || [], newCache);
              return newCache;
            });
          }
        } catch (error) {
          console.error('Error refreshing parent children:', error);
        }
      }

      closeCreateModal();
      toast({
        title: 'Success',
        description: 'Transaction created successfully!',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to create transaction: ${error?.message || String(error)}`,
        variant: 'destructive',
      });
    },
  });

  // Helper function to close create modal and reset state
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateParentId(null);
  };

  // Helper function to collect all child transaction IDs recursively
  // Uses childrenCache to find all nested children
  const collectAllChildIds = (parentId: number): number[] => {
    const ids: number[] = [];

    const collectRecursive = (txnId: number) => {
      // Get children from cache
      const children = childrenCache.get(txnId);
      if (children && children.length > 0) {
        for (const child of children) {
          ids.push(child.id);
          // Recursively collect grandchildren
          collectRecursive(child.id);
        }
      }
    };

    collectRecursive(parentId);
    return ids;
  };

  // Verify transaction mutation - verifies parent and all children recursively
  const verifyMutation = useMutation({
    mutationFn: async (transactionId: number) => {
      // Verify the parent transaction
      await financialTransactionApi.verifyTransaction(transactionId);

      // Collect all child IDs recursively from cache
      const childIds = collectAllChildIds(transactionId);

      // Verify all children recursively
      if (childIds.length > 0) {
        console.log(`[verifyMutation] Verifying ${childIds.length} children recursively:`, childIds);
        await Promise.all(
          childIds.map(childId => financialTransactionApi.verifyTransaction(childId))
        );
      }

      return { transactionId, childIds };
    },
    onSuccess: async ({ transactionId, childIds }) => {
      // Invalidate and refetch queries to refresh the table
      await queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
      await queryClient.refetchQueries({ queryKey: ['financialTransactions'] });

      // Update the verified status in the children cache for immediate UI update
      if (childIds.length > 0) {
        setChildrenCache((prevCache) => {
          const newCache = new Map(prevCache);

          // Update verified status for all children in cache
          const updateChildrenVerified = (parentId: number) => {
            const children = newCache.get(parentId);
            if (children) {
              const updatedChildren = children.map(child => ({
                ...child,
                verified: true
              }));
              newCache.set(parentId, updatedChildren);

              // Recursively update grandchildren
              for (const child of updatedChildren) {
                updateChildrenVerified(child.id);
              }
            }
          };

          updateChildrenVerified(transactionId);
          return newCache;
        });
      }

      // If this transaction has a parent, also refresh the parent's children cache
      // This ensures parent rows show updated child status
      if (selectedTransaction?.parent_transaction_id) {
        try {
          const parentWithChildren = await financialTransactionApi.getTransaction(
            selectedTransaction.parent_transaction_id,
            true,
            false
          );
          if (parentWithChildren.children) {
            setChildrenCache((prevCache) => {
              const newCache = new Map(prevCache);
              cacheAllChildren(selectedTransaction.parent_transaction_id!, parentWithChildren.children || [], newCache);
              return newCache;
            });
          }
        } catch (error) {
          console.error('Error refreshing parent children cache:', error);
        }
      }

      if (selectedTransaction) {
        // Reload the transaction details to show updated verification status (includes children)
        const updated = await financialTransactionApi.getTransaction(selectedTransaction.id, true, false);
        setSelectedTransaction(updated);
      }

      // Show success message with count of children verified
      const message = childIds.length > 0
        ? `Transaction verified successfully! Also verified ${childIds.length} child transaction${childIds.length === 1 ? '' : 's'}.`
        : 'Transaction verified successfully!';
      alert(message);
    },
    onError: (error: any) => {
      alert(`Failed to verify transaction: ${error?.message || String(error)}`);
    },
  });

  // Execute transaction mutation - REMOVED: transactions now auto-execute when balance is released
  // const executeMutation = useMutation({
  //   mutationFn: (transactionId: number) =>
  //     financialTransactionApi.executeTransaction(transactionId),
  //   onSuccess: async (_, transactionId) => {
  //     // Always fetch and cache direct children for the executed transaction
  //     // This ensures they're available when the row is expanded, including any newly created nested children
  //     try {
  //       const txnWithChildren = await financialTransactionApi.getTransaction(transactionId, true, false);
  //       if (txnWithChildren.children) {
  //         setChildrenCache((prevCache) => {
  //           const newCache = new Map(prevCache);
  //           cacheAllChildren(transactionId, txnWithChildren.children || [], newCache);
  //           return newCache;
  //         });
  //       }
  //     } catch (error) {
  //       console.error('Error refreshing children cache:', error);
  //     }
  //
  //     // Invalidate and refetch queries to refresh the table
  //     await queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
  //     await queryClient.refetchQueries({ queryKey: ['financialTransactions'] });
  //
  //     // If this transaction has a parent, also refresh the parent's children cache
  //     // This handles cases where executing a child creates grandchildren
  //     if (selectedTransaction?.parent_transaction_id) {
  //       try {
  //         const parentWithChildren = await financialTransactionApi.getTransaction(
  //           selectedTransaction.parent_transaction_id,
  //           true,
  //           false
  //         );
  //         if (parentWithChildren.children) {
  //           setChildrenCache((prevCache) => {
  //             const newCache = new Map(prevCache);
  //             cacheAllChildren(selectedTransaction.parent_transaction_id!, parentWithChildren.children || [], newCache);
  //             return newCache;
  //           });
  //         }
  //       } catch (error) {
  //         console.error('Error refreshing parent children cache:', error);
  //       }
  //     }
  //
  //     if (selectedTransaction) {
  //       // Reload the transaction details to show updated status and any new child transactions
  //       const updated = await financialTransactionApi.getTransaction(selectedTransaction.id, true, false);
  //       setSelectedTransaction(updated);
  //     }
  //     alert('Transaction executed successfully!');
  //   },
  //   onError: (error: any) => {
  //     alert(`Failed to execute transaction: ${error?.message || String(error)}`);
  //   },
  // });

  // Release funds mutation
  const releaseFundsMutation = useMutation({
    mutationFn: ({ accountId, amount, batchId, newBatchName }: {
      accountId: number;
      amount: number;
      batchId?: number | null;
      newBatchName?: string;
    }) =>
      financialAccountApi.releaseFunds(accountId, amount, {
        batch_id: batchId,
        new_batch_name: newBatchName,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['financialAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['availableBatchesForRelease'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalBatches'] });
      setShowReleaseFundsDialog(false);
      setReleaseAmount('');
      setSelectedAccountForRelease(null);
      setSelectedBatchId(null);
      setNewBatchName('');
      toast({
        title: 'Success',
        description: `Funds released successfully! Added to batch ${data.batch_number}`,
      });
    },
    onError: (error: any) => {
      console.error('[ReleaseFunds] Error:', error);
      // Keep dialog open to show error - error will be displayed in the dialog UI
    },
  });

  // Sync Stripe balance mutation
  const syncStripeBalanceMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-stripe-balance', {
        method: 'POST',
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['financialAccounts'] });
      const usdBalance = data?.balances?.USD;
      if (usdBalance) {
        alert(`Stripe balance synced successfully!\n\nCurrent: $${usdBalance.current?.toFixed(2) || 0}\nAvailable: $${usdBalance.available?.toFixed(2) || 0}\nPending: $${usdBalance.pending?.toFixed(2) || 0}`);
      } else {
        alert('Stripe balance synced successfully!');
      }
    },
    onError: (error: any) => {
      alert(`Failed to sync Stripe balance: ${error?.message || String(error)}`);
    },
  });

  // Handle release funds
  const handleReleaseFunds = (account: any) => {
    setSelectedAccountForRelease(account);
    // Prefill with available balance
    const availableBalance = account.balances?.find((b: any) => b.balance_type === 'available')?.amount || 0;
    setReleaseAmount(availableBalance.toString());
    setSelectedBatchId(null);
    setNewBatchName('');
    setShowReleaseFundsDialog(true);
  };

  const handleReleaseFundsSubmit = () => {
    if (!selectedAccountForRelease) return;
    const amount = parseFloat(releaseAmount);
    console.log('[ReleaseFunds] Parsed amount:', amount, 'from releaseAmount:', releaseAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    const availableBalance = selectedAccountForRelease.balances?.find((b: any) => b.balance_type === 'available')?.amount || 0;
    if (amount > availableBalance) {
      alert(`Amount exceeds available balance ($${availableBalance.toFixed(2)})`);
      return;
    }
    if (selectedBatchId === 'new' && !newBatchName.trim()) {
      alert('Please enter a name for the new batch');
      return;
    }
    // Proceed without confirmation alert
    console.log('[ReleaseFunds] Calling mutation with:', {
      accountId: selectedAccountForRelease.id,
      amount,
      batchId: selectedBatchId === 'new' ? null : selectedBatchId,
      newBatchName: selectedBatchId === 'new' ? newBatchName : undefined,
    });
    releaseFundsMutation.mutate({
      accountId: selectedAccountForRelease.id,
      amount,
      batchId: selectedBatchId === 'new' ? null : selectedBatchId,
      newBatchName: selectedBatchId === 'new' ? newBatchName : undefined,
    });
  };

  // Handle download statement
  const handleDownloadStatement = async (account: any) => {
    try {
      toast({
        title: "Generating statement...",
        description: "Please wait while we fetch transactions.",
      });

      // Fetch child transactions that affect the available balance for this account with pagination
      let allTransactions: any[] = [];
      let offset = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: transactions, error } = await supabase
          .from('financial_transactions')
          .select(`
            id,
            transaction_date,
            transaction_type,
            amount,
            already_paid,
            status,
            account_from_id,
            account_to_id,
            parent_transaction_id,
            reference_type,
            reference_id,
            provider_reference,
            created_at,
            metadata,
            account_from:financial_accounts!financial_transactions_account_from_id_fkey(name, account_type),
            account_to:financial_accounts!financial_transactions_account_to_id_fkey(name, account_type),
            parent:financial_transactions!parent_transaction_id(id, transaction_type, amount, reference_type, reference_id, provider_reference)
          `)
          .eq('status', 'executed')
          .not('parent_transaction_id', 'is', null)
          .or(`account_from_id.eq.${account.id},account_to_id.eq.${account.id}`)
          .order('transaction_date', { ascending: true })
          .order('id', { ascending: true })
          .range(offset, offset + pageSize - 1);

        if (error) throw error;

        if (transactions && transactions.length > 0) {
          allTransactions = allTransactions.concat(transactions);
          offset += pageSize;
          hasMore = transactions.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // Sort transactions: by date, then receipts before fee distributions, then by id
      const getTransactionOrder = (txn: any) => {
        const metadata = txn.metadata as any;
        // Receipts (money coming in) should be processed first
        if (metadata?.is_receipt || metadata?.step === 'stripe_to_platform') return 0;
        // Then fee distributions
        if (txn.transaction_type === 'fee_distribution') return 1;
        // Everything else
        return 2;
      };

      allTransactions.sort((a, b) => {
        // First by transaction_date
        const dateA = new Date(a.transaction_date).getTime();
        const dateB = new Date(b.transaction_date).getTime();
        if (dateA !== dateB) return dateA - dateB;

        // Then by parent_transaction_id (group children together)
        if (a.parent_transaction_id !== b.parent_transaction_id) {
          return (a.parent_transaction_id || 0) - (b.parent_transaction_id || 0);
        }

        // Then receipts before fee distributions
        const orderA = getTransactionOrder(a);
        const orderB = getTransactionOrder(b);
        if (orderA !== orderB) return orderA - orderB;

        // Finally by id
        return a.id - b.id;
      });

      const transactions = allTransactions;
      if (!transactions || transactions.length === 0) {
        toast({
          title: "No transactions found",
          description: "This account has no transaction history to export.",
          variant: "destructive",
        });
        return;
      }

      // Calculate running balance
      let runningBalance = 0;
      const csvRows = [];

      // CSV Header
      csvRows.push([
        'ID',
        'Transaction Date',
        'Transaction Type',
        'Amount',
        'Already Paid',
        'Status',
        'From Account',
        'From Type',
        'To Account',
        'To Type',
        'Account Affected',
        'Account Affected Type',
        'Balance Before',
        'Balance After',
        'Balance Change',
        'Parent Transaction ID',
        'Parent Type',
        'Parent Amount',
        'Parent Reference Type',
        'Parent Reference ID',
        'Parent Provider Reference',
        'Reference Type',
        'Reference ID',
        'Payment ID',
        'Provider Reference',
        'Created At'
      ].join(','));

      // Format row (escape commas and quotes for CSV)
      const escapeCSV = (val: any) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Process transactions
      for (const txn of transactions) {
        const balanceBefore = runningBalance;
        let balanceChange = 0;

        // Only calculate balance change for transactions that haven't been paid out yet
        if (!txn.already_paid) {
          // Calculate balance change based on which account is affected
          if (txn.account_from_id === account.id && txn.account_to_id === account.id) {
            // Same account - no net change
            balanceChange = 0;
          } else if (txn.account_to_id === account.id) {
            // Money coming IN
            balanceChange = txn.amount;
          } else if (txn.account_from_id === account.id) {
            // Money going OUT
            balanceChange = -txn.amount;
          }
        }

        const balanceAfter = balanceBefore + balanceChange;
        runningBalance = balanceAfter;

        const parent = (txn as any).parent;
        const metadata = txn.metadata as any;

        csvRows.push([
          txn.id,
          txn.transaction_date,
          txn.transaction_type,
          txn.amount,
          txn.already_paid,
          txn.status,
          escapeCSV(txn.account_from?.name || ''),
          escapeCSV(txn.account_from?.account_type || ''),
          escapeCSV(txn.account_to?.name || ''),
          escapeCSV(txn.account_to?.account_type || ''),
          escapeCSV(account.display_name || account.reference || ''),
          escapeCSV(account.account_type),
          balanceBefore.toFixed(2),
          balanceAfter.toFixed(2),
          balanceChange.toFixed(2),
          txn.parent_transaction_id || '',
          escapeCSV(parent?.transaction_type || ''),
          parent?.amount || '',
          escapeCSV(parent?.reference_type || ''),
          parent?.reference_id || '',
          escapeCSV(parent?.provider_reference || ''),
          txn.reference_type || '',
          txn.reference_id || '',
          metadata?.payment_id || '',
          txn.provider_reference || '',
          txn.created_at
        ].join(','));
      }

      // Create CSV file and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement_${account.display_name || account.reference || account.id}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Statement downloaded",
        description: `Successfully exported ${transactions.length} transactions.`,
      });
    } catch (error: any) {
      console.error('Download statement error:', error);
      toast({
        title: "Failed to download statement",
        description: error?.message || String(error),
        variant: "destructive",
      });
    }
  };

  // Download all account statements as a single CSV
  const handleDownloadAllStatements = async () => {
    try {
      toast({
        title: "Generating combined statement...",
        description: "Please wait while we fetch all transactions.",
      });

      // Get all accounts to process (filtered by current filters)
      const accountsToExport = filteredBalanceAccounts;
      if (!accountsToExport || accountsToExport.length === 0) {
        toast({
          title: "No accounts found",
          description: "No accounts match your current filters.",
          variant: "destructive",
        });
        return;
      }

      // Fetch all transactions for all filtered accounts with pagination
      const accountIds = accountsToExport.map(a => a.id);
      let allTransactions: any[] = [];
      let offset = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: transactions, error } = await supabase
          .from('financial_transactions')
          .select(`
            id,
            transaction_date,
            transaction_type,
            amount,
            already_paid,
            status,
            account_from_id,
            account_to_id,
            parent_transaction_id,
            reference_type,
            reference_id,
            provider_reference,
            created_at,
            metadata,
            account_from:financial_accounts!financial_transactions_account_from_id_fkey(name, account_type),
            account_to:financial_accounts!financial_transactions_account_to_id_fkey(name, account_type)
          `)
          .eq('status', 'executed')
          .not('parent_transaction_id', 'is', null)
          .or(accountIds.map(id => `account_from_id.eq.${id},account_to_id.eq.${id}`).join(','))
          .order('transaction_date', { ascending: true })
          .order('id', { ascending: true })
          .range(offset, offset + pageSize - 1);

        if (error) throw error;

        if (transactions && transactions.length > 0) {
          allTransactions = allTransactions.concat(transactions);
          offset += pageSize;
          hasMore = transactions.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // Sort transactions: by date, then receipts before fee distributions, then by id
      const getTransactionOrder = (txn: any) => {
        const metadata = txn.metadata as any;
        // Receipts (money coming in) should be processed first
        if (metadata?.is_receipt || metadata?.step === 'stripe_to_platform') return 0;
        // Then fee distributions
        if (txn.transaction_type === 'fee_distribution') return 1;
        // Everything else
        return 2;
      };

      allTransactions.sort((a, b) => {
        // First by transaction_date
        const dateA = new Date(a.transaction_date).getTime();
        const dateB = new Date(b.transaction_date).getTime();
        if (dateA !== dateB) return dateA - dateB;

        // Then by parent_transaction_id (group children together)
        if (a.parent_transaction_id !== b.parent_transaction_id) {
          return (a.parent_transaction_id || 0) - (b.parent_transaction_id || 0);
        }

        // Then receipts before fee distributions
        const orderA = getTransactionOrder(a);
        const orderB = getTransactionOrder(b);
        if (orderA !== orderB) return orderA - orderB;

        // Finally by id
        return a.id - b.id;
      });

      const transactions = allTransactions;
      if (!transactions || transactions.length === 0) {
        toast({
          title: "No transactions found",
          description: "No transaction history to export for the selected accounts.",
          variant: "destructive",
        });
        return;
      }

      // Group transactions by account and calculate running balances
      const accountMap = new Map(accountsToExport.map(a => [a.id, a]));
      const runningBalances = new Map<number, number>();
      accountIds.forEach(id => runningBalances.set(id, 0));

      const csvRows = [];

      // CSV Header
      csvRows.push([
        'ID',
        'Transaction Date',
        'Transaction Type',
        'Amount',
        'Already Paid',
        'Status',
        'From Account',
        'From Type',
        'To Account',
        'To Type',
        'Account Affected',
        'Account Affected Type',
        'Balance Before',
        'Balance After',
        'Balance Change',
        'Parent Transaction ID',
        'Reference Type',
        'Reference ID',
        'Payment ID',
        'Provider Reference',
        'Created At'
      ].join(','));

      // Escape CSV value helper
      const escapeCSV = (val: any) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Process transactions - each transaction may affect multiple accounts
      for (const txn of transactions) {
        // Get list of accounts affected by this transaction
        const affectedAccountIds: number[] = [];

        // Check if from_account is in our export list
        if (accountIds.includes(txn.account_from_id)) {
          affectedAccountIds.push(txn.account_from_id);
        }
        // Check if to_account is in our export list (and different from from_account)
        if (accountIds.includes(txn.account_to_id) && txn.account_to_id !== txn.account_from_id) {
          affectedAccountIds.push(txn.account_to_id);
        }
        // Handle self-transfers (same account from and to)
        if (txn.account_from_id === txn.account_to_id && accountIds.includes(txn.account_from_id)) {
          // Already added above, just continue with one entry (no balance change)
          if (affectedAccountIds.length === 0) {
            affectedAccountIds.push(txn.account_from_id);
          }
        }

        // Create a row for each affected account
        for (const affectedAccountId of affectedAccountIds) {
          const affectedAccount = accountMap.get(affectedAccountId);
          if (!affectedAccount) continue;

          const balanceBefore = runningBalances.get(affectedAccountId) || 0;
          let balanceChange = 0;

          // Only calculate balance change for transactions that haven't been paid out yet
          if (!txn.already_paid) {
            // Calculate balance change for this specific account
            if (txn.account_from_id === affectedAccountId && txn.account_to_id === affectedAccountId) {
              // Self-transfer - no net change
              balanceChange = 0;
            } else if (txn.account_to_id === affectedAccountId) {
              // Money coming IN to this account
              balanceChange = txn.amount;
            } else if (txn.account_from_id === affectedAccountId) {
              // Money going OUT from this account
              balanceChange = -txn.amount;
            }
          }

          const balanceAfter = balanceBefore + balanceChange;
          runningBalances.set(affectedAccountId, balanceAfter);

          const metadata = txn.metadata as any;

          csvRows.push([
            txn.id,
            txn.transaction_date,
            txn.transaction_type,
            txn.amount,
            txn.already_paid,
            txn.status,
            escapeCSV(txn.account_from?.name || ''),
            escapeCSV(txn.account_from?.account_type || ''),
            escapeCSV(txn.account_to?.name || ''),
            escapeCSV(txn.account_to?.account_type || ''),
            escapeCSV(affectedAccount.display_name || affectedAccount.reference || ''),
            escapeCSV(affectedAccount.account_type),
            balanceBefore.toFixed(2),
            balanceAfter.toFixed(2),
            balanceChange.toFixed(2),
            txn.parent_transaction_id || '',
            txn.reference_type || '',
            txn.reference_id || '',
            metadata?.payment_id || '',
            txn.provider_reference || '',
            txn.created_at
          ].join(','));
        }
      }

      // Create CSV file and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all_statements_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Combined statement downloaded",
        description: `Successfully exported ${transactions.length} transactions from ${accountsToExport.length} accounts.`,
      });
    } catch (error: any) {
      console.error('Download all statements error:', error);
      toast({
        title: "Failed to download statements",
        description: error?.message || String(error),
        variant: "destructive",
      });
    }
  };

  // Open statement export dialog for a specific account
  const openStatementExportDialog = (account: any) => {
    setStatementExportAccountId(account.id);
    setShowStatementExportDialog(true);
  };

  // Open statement export dialog for all accounts
  const openStatementExportDialogForAll = () => {
    setStatementExportAccountId(null);
    setShowStatementExportDialog(true);
  };

  // Format statement month for display
  const formatStatementMonth = (monthStr: string): string => {
    // Parse the date string as local time to avoid timezone issues
    // monthStr is in format "2025-12-01"
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(year, month - 1, 1); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  // Handle statement export
  const handleStatementExport = async () => {
    if (!selectedStatementId) {
      toast({
        title: 'Error',
        description: 'Please select a statement period',
        variant: 'destructive',
      });
      return;
    }

    setIsExportingStatement(true);

    try {
      const statement = statements?.find((s) => s.id === selectedStatementId);
      const statementNumber = statement?.statement_number || 'statement';
      const account = statementExportAccountId
        ? filteredBalanceAccounts?.find((a) => a.id === statementExportAccountId)
        : null;
      const accountName = account?.display_name || account?.reference;

      if (statementExportFormat === 'csv') {
        // Get CSV content from API
        let csvContent = await financialStatementApi.exportStatementCSV(
          selectedStatementId,
          statementExportAccountId || undefined
        );

        // Filter out Platform account rows if unchecked (only when exporting all accounts)
        if (!includePlatform && !statementExportAccountId) {
          const lines = csvContent.split('\n');
          const header = lines[0];
          // "Account Type" is the 5th column (index 4) in the CSV exported by the backend
          const filteredLines = lines.slice(1).filter((line) => {
            if (!line.trim()) return true; // preserve trailing blank lines
            const match = line.match(/(?:^|,)("(?:[^"]|"")*"|[^,]*)/g);
            if (!match || match.length < 5) return true;
            const accountTypeField = match[4].replace(/^,/, '').replace(/^"|"$/g, '');
            return accountTypeField !== 'platform';
          });
          csvContent = [header, ...filteredLines].join('\n');
        }

        downloadStatementCSV(csvContent, statementNumber, accountName || undefined);
        toast({
          title: 'Export Successful',
          description: `Statement exported as CSV`,
        });
      } else {
        // Get data for PDF generation
        let exportData = await financialStatementApi.getStatementExportData(
          selectedStatementId,
          statementExportAccountId || undefined
        );

        // Filter out Platform accounts if checkbox is unchecked
        if (!includePlatform && !statementExportAccountId) {
          const nonPlatformAccounts = exportData.accounts.filter(
            (a) => a.account_type !== 'platform'
          );
          const nonPlatformIds = new Set(nonPlatformAccounts.map((a) => a.id));
          exportData = {
            ...exportData,
            accounts: nonPlatformAccounts,
            snapshots: exportData.snapshots.filter((s) => nonPlatformIds.has(s.account_id)),
          };
        }

        // Filter out accounts with no transactions if checkbox is unchecked
        if (!includeEmptyAccounts && !statementExportAccountId) {
          // Filter accounts that have activity in this period
          const filteredAccounts = exportData.accounts.filter((account) => {
            // Check if this account has any transactions in this period
            const hasTransactions = exportData.transactions.some(
              (t) => t.account_from_id === account.id || t.account_to_id === account.id
            );
            if (hasTransactions) return true;

            // Check if this account has any credits or debits in the snapshot
            const snapshot = exportData.snapshots.find((s) => s.account_id === account.id);
            if (snapshot) {
              const credits = Number(snapshot.total_credits) || 0;
              const debits = Number(snapshot.total_debits) || 0;
              if (credits > 0 || debits > 0) return true;
            }

            return false;
          });

          // Get IDs of accounts to keep
          const accountIdsToKeep = new Set(filteredAccounts.map((a) => a.id));

          // Filter snapshots to match filtered accounts
          exportData = {
            ...exportData,
            accounts: filteredAccounts,
            snapshots: exportData.snapshots.filter((s) => accountIdsToKeep.has(s.account_id)),
          };
        }

        if (statementExportAccountId) {
          // Export single account PDF
          await downloadStatementPDF(exportData, statementExportAccountId);
          toast({
            title: 'Export Successful',
            description: `Statement exported as PDF`,
          });
        } else {
          // Export all accounts - generate single combined PDF
          await downloadAllAccountsStatementPDF(exportData);
          toast({
            title: 'Export Successful',
            description: `Combined statement PDF exported for all accounts`,
          });
        }
      }

      setShowStatementExportDialog(false);
      setSelectedStatementId(null);
      setStatementExportAccountId(null);
      setIncludePlatform(true);
    } catch (error) {
      console.error('Statement export error:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export statement',
        variant: 'destructive',
      });
    } finally {
      setIsExportingStatement(false);
    }
  };

  // Handle upload wire transfer results
  const handleUploadWireResults = async () => {
    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      // TODO: Implement the actual upload and processing logic
      // This will:
      // 1. Parse the uploaded file (CSV/Excel)
      // 2. Match wire transfer references to pending withdrawal transactions
      // 3. Update transaction statuses to 'executed'
      // 4. Update actual amounts if different from expected
      // 5. Send email notifications to management companies

      alert('Wire transfer results upload is not yet implemented. This will:\n\n' +
            '1. Parse the uploaded file\n' +
            '2. Match references to pending withdrawals\n' +
            '3. Mark transactions as executed\n' +
            '4. Send email notifications');

      setShowUploadDialog(false);
      setUploadFile(null);
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(`Failed to upload wire transfer results: ${error?.message || String(error)}`);
    } finally {
      setIsUploading(false);
    }
  };

  // View transaction details
  const handleViewTransaction = async (txn: FinancialTransaction) => {
    const details = await financialTransactionApi.getTransaction(txn.id, true, false);
    setSelectedTransaction(details);
  };

  // Sync Stripe transactions
  const handleSyncStripe = async () => {
    setIsSyncing(true);
    try {
      // The client targets this environment's project with the current session.
      const { data, error } = await supabase.functions.invoke('comprehensive-financial-sync', {
        method: 'POST',
        body: { clear_existing: false },
      });
      if (error) throw error;

      if (data.success) {
        alert(`Sync completed!\n\nOrders processed: ${data.stats?.orders_processed || 0}\nTransactions created: ${data.stats?.transactions_created || 0}\nStripe charges: ${data.stats?.stripe_charges_fetched || 0}\nPayouts: ${data.stats?.payouts_processed || 0}\nErrors: ${data.stats?.errors || 0}`);
        queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
      } else {
        alert(`Sync failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      alert(`Sync failed: ${error?.message || String(error)}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Populate charge IDs from Stripe checkout sessions
  const handlePopulateChargeIds = async () => {
    setIsPopulating(true);
    try {
      const { data, error } = await supabase.functions.invoke('populate-charge-ids', {
        method: 'POST',
      });
      if (error) throw error;

      if (data.success) {
        alert(`Charge ID population completed!\n\nTotal: ${data.stats.total}\nUpdated: ${data.stats.updated}\nNot found: ${data.stats.notFound}\nSkipped: ${data.stats.skipped}\nErrors: ${data.stats.errors}\n\nNow run "Sync Stripe" to match transactions.`);
      } else {
        alert(`Population failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Populate error:', error);
      alert(`Population failed: ${error?.message || String(error)}`);
    } finally {
      setIsPopulating(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Format account name
  const formatAccountName = (account: any): string => {
    if (!account) return '-';

    // If there's a custom name, use it
    if (account.name) return account.name;

    // Format based on account type
    switch (account.account_type) {
      case 'stripe':
        return 'Stripe';
      case 'payment_gateway':
        // Handle payment gateway accounts like Stripe
        if (account.reference === 'stripe') return 'Stripe';
        if (account.reference === 'external') return 'External Payments';
        if (account.reference === 'bank') return 'Bank Account';
        return account.reference || 'Payment Gateway';
      case 'platform':
        return 'Dommek Platform';
      case 'association':
        return account.reference
          ? `Association #${account.reference}`
          : 'Association';
      case 'management_company':
        return account.reference
          ? `Management Company #${account.reference}`
          : 'Management Company';
      case 'bank':
        return account.reference
          ? `Bank Account (${account.reference})`
          : 'Bank Account';
      default:
        if (account.reference) {
          return `${account.account_type} (${account.reference})`;
        }
        return account.account_type || '-';
    }
  };

  // Format step label with proper capitalization
  const formatStepLabel = (step: string): string => {
    return step
      .replace(/_/g, ' ')
      .replace(/\bmc\b/gi, 'Management Company')
      .split(' ')
      .map((word, index) => {
        // Keep 'to' lowercase unless it's the first word
        if (word.toLowerCase() === 'to' && index > 0) return 'to';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  // Get status badge color
  const getStatusColor = (status: TransactionStatus): string => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'executed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if transaction has amount discrepancy
  const hasDiscrepancy = (txn: FinancialTransaction): boolean => {
    return txn.expected_amount !== null &&
           txn.expected_amount !== undefined &&
           Math.abs(txn.amount - txn.expected_amount) > 0.01;
  };

  // Export transactions to CSV
  const handleExportTransactions = async () => {
    try {
      setIsExporting(true);

      // Use the Edge Function endpoint which has pagination support
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No authentication session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financial-transactions?parents_only=true&export=true`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'x-user-role': JSON.stringify({ type: 'platform', entityName: 'Platform' })
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Invalid response format');

      // Convert to CSV
      const headers = [
        'id', 'transaction_type', 'transaction_date', 'amount', 'expected_amount',
        'currency', 'status', 'provider', 'provider_reference',
        'reference_type', 'reference_id', 'already_paid',
        'account_from_type', 'account_from_reference',
        'account_to_type', 'account_to_reference'
      ];

      const csvRows = [headers.join(',')];

      data?.forEach(txn => {
        const row = [
          txn.id,
          txn.transaction_type || '',
          txn.transaction_date || '',
          txn.amount || '',
          txn.expected_amount || '',
          txn.currency || '',
          txn.status || '',
          txn.provider || '',
          txn.provider_reference || '',
          txn.reference_type || '',
          txn.reference_id || '',
          txn.already_paid || false,
          txn.account_from?.account_type || '',
          txn.account_from?.reference || '',
          txn.account_to?.account_type || '',
          txn.account_to?.reference || ''
        ];
        csvRows.push(row.map(v => `"${v}"`).join(','));
      });

      // Download CSV
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: `Exported ${data?.length || 0} transactions to CSV`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to export transactions",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Import CSV to mark transactions as already_paid
  const handleImportFile = async () => {
    if (!importFile) return;

    try {
      setIsImporting(true);
      const text = await importFile.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

      const idIndex = headers.indexOf('id');
      if (idIndex === -1) {
        throw new Error('CSV must contain an "id" column');
      }

      const transactionIds: number[] = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const id = parseInt(values[idIndex]);
        if (!isNaN(id)) {
          transactionIds.push(id);
        }
      }

      if (transactionIds.length === 0) {
        throw new Error('No valid transaction IDs found in CSV');
      }

      // Mark transactions as already_paid
      const { error } = await supabase
        .from('financial_transactions')
        .update({ already_paid: true, updated_at: new Date().toISOString() })
        .in('id', transactionIds)
        .is('parent_transaction_id', null);

      if (error) throw error;

      toast({
        title: "Import Successful",
        description: `Marked ${transactionIds.length} transactions as already paid`,
      });

      setShowImportDialog(false);
      setImportFile(null);
      queryClient.invalidateQueries({ queryKey: ['financialTransactions'] });
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import transactions",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Financial Transactions</h1>
          <p className="text-gray-600">Review and approve financial transactions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setCreateParentId(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Transaction
          </button>
      </div>
    </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('transactions')}
              className={`${
                activeTab === 'transactions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('balances')}
              className={`${
                activeTab === 'balances'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Account Balances
            </button>
          </nav>
        </div>
      </div>

      {/* Account Balances Tab */}
      {activeTab === 'balances' && (
        <>
          {/* Header */}
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Account Balances</h2>
            <div className="flex gap-3">
              <button
                onClick={openStatementExportDialogForAll}
                disabled={!statements || statements.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Download className="w-5 h-5 mr-2" />
                Export All
              </button>
              <button
                onClick={() => setShowBatchesDialog(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Batches
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Accounts
                </label>
                <input
                  type="text"
                  value={balanceSearchTerm}
                  onChange={(e) => setBalanceSearchTerm(e.target.value)}
                  placeholder="Search by name or type..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Account Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type
                </label>
                <select
                  value={balanceAccountTypeFilter}
                  onChange={(e) => setBalanceAccountTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="">All Types</option>
                  <option value="platform">Platform</option>
                  <option value="association">Association</option>
                  <option value="management_company">Management Company</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={balanceStatusFilter}
                  onChange={(e) => setBalanceStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Balance Amount Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Balance
                </label>
                <select
                  value={balanceAmountFilter}
                  onChange={(e) => setBalanceAmountFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Balances</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                  <option value="zero">Zero</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(balanceSearchTerm || balanceAccountTypeFilter || balanceStatusFilter || balanceAmountFilter !== 'all') && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => {
                    setBalanceSearchTerm('');
                    setBalanceAccountTypeFilter('');
                    setBalanceStatusFilter('');
                    setBalanceAmountFilter('all');
                  }}
                  className="text-sm text-primary hover:text-primary-dark"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Results count */}
          <div className="mb-4 text-sm text-gray-600">
            Showing {filteredBalanceAccounts.length} of {filteredAccounts?.length || 0} accounts
          </div>

          {/* Table */}
          {filteredBalanceAccounts.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Available Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Current Balance
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBalanceAccounts.map(account => {
                const currentBalance = account.balances?.find(b => b.balance_type === 'current');
                const availableBalance = account.balances?.find(b => b.balance_type === 'available');

                return (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs truncate" title={account.display_name || account.reference || 'Unknown'}>
                      {account.display_name || account.reference || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.account_type.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <span className={`font-semibold ${(availableBalance?.amount || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${(availableBalance?.amount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      ${(currentBalance?.amount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        account.status === 'active' ? 'bg-green-100 text-green-800' :
                        account.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {account.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {(account.account_type === 'management_company' || account.account_type === 'association' || account.account_type === 'platform') && (
                          <button
                            onClick={() => handleReleaseFunds(account)}
                            disabled={(availableBalance?.amount || 0) <= 0}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded ${
                              (availableBalance?.amount || 0) > 0
                                ? 'text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer'
                                : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                            }`}
                          >
                            Release Funds
                          </button>
                        )}
                        <button
                          onClick={() => openStatementExportDialog(account)}
                          className="inline-flex items-center p-2 border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                          title="Export Statement"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No accounts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters to see more results
            </p>
          </div>
        )}
        </>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <>

      {/* Summary Statistics - Single Horizontal Card */}
      {summaryStats && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <div className="flex flex-wrap items-center gap-8">
            {/* Matched & Correct - Clickable - FIRST */}
            <button
              onClick={() => setDiscrepancyFilter(discrepancyFilter === 'matched' ? 'all' : 'matched')}
              className={`flex flex-col items-start transition-colors hover:bg-green-50 p-3 rounded-lg ${discrepancyFilter === 'matched' ? 'bg-green-100' : ''}`}
            >
              <div className="text-sm text-gray-600 mb-1">Matched & Correct</div>
              <div className="text-3xl font-bold text-green-600">
                {summaryStats.matchedCount - summaryStats.discrepancyCount}
              </div>
            </button>

            <div className="h-12 w-px bg-gray-300"></div>

            {/* Issues Found - Clickable - SECOND */}
            <button
              onClick={() => setDiscrepancyFilter(discrepancyFilter === 'issues' ? 'all' : 'issues')}
              className={`flex flex-col items-start transition-colors hover:bg-orange-50 p-3 rounded-lg ${discrepancyFilter === 'issues' ? 'bg-orange-100' : ''}`}
            >
              <div className="text-sm text-gray-600 mb-1">Issues Found</div>
              <div className={`text-3xl font-bold ${summaryStats.discrepancyCount + summaryStats.unmatchedCount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {summaryStats.discrepancyCount + summaryStats.unmatchedCount}
              </div>
            </button>

            <div className="h-12 w-px bg-gray-300"></div>

            {/* Amount Mismatches - Clickable - SMALLER */}
            <button
              onClick={() => setDiscrepancyFilter(discrepancyFilter === 'mismatches' ? 'all' : 'mismatches')}
              className={`flex flex-col items-start transition-colors hover:bg-orange-50 p-3 rounded-lg ${discrepancyFilter === 'mismatches' ? 'bg-orange-100' : ''}`}
            >
              <div className="text-xs text-gray-500 mb-1">Amount Mismatches</div>
              <div className="text-xl font-semibold text-orange-600">
                {summaryStats.discrepancyCount}
              </div>
            </button>

            <div className="h-12 w-px bg-gray-300"></div>

            {/* Unmatched - Clickable - SMALLER */}
            <button
              onClick={() => setDiscrepancyFilter(discrepancyFilter === 'unmatched' ? 'all' : 'unmatched')}
              className={`flex flex-col items-start transition-colors hover:bg-orange-50 p-3 rounded-lg ${discrepancyFilter === 'unmatched' ? 'bg-orange-100' : ''}`}
            >
              <div className="text-xs text-gray-500 mb-1">Unmatched</div>
              <div className="text-xl font-semibold text-orange-600">
                {summaryStats.unmatchedCount}
              </div>
            </button>

            <div className="h-12 w-px bg-gray-300"></div>

            {/* Variance - Clickable - SMALLER */}
            <button
              onClick={() => setDiscrepancyFilter(discrepancyFilter === 'issues' ? 'all' : 'issues')}
              className={`flex flex-col items-start transition-colors hover:bg-blue-50 p-3 rounded-lg ${discrepancyFilter === 'issues' ? 'bg-blue-100' : ''}`}
            >
              <div className="text-xs text-gray-500 mb-1">Variance</div>
              <div className={`text-xl font-semibold ${summaryStats.variance < -0.01 ? 'text-red-600' : summaryStats.variance > 0.01 ? 'text-orange-600' : 'text-green-600'}`}>
                {summaryStats.variance >= 0 ? '+' : ''}{formatCurrency(summaryStats.variance)}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as TransactionStatus | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="executed">Executed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Verified Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verified
            </label>
            <select
              value={selectedVerified}
              onChange={(e) => setSelectedVerified(e.target.value as 'all' | 'yes' | 'no')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as TransactionType | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="order_payment">Order Payment</option>
              <option value="fee_distribution">Fee Distribution</option>
              <option value="payout">Payout</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="refund">Refund</option>
              <option value="dispute">Dispute</option>
              <option value="manual_adjustment">Manual Adjustment</option>
            </select>
          </div>

          {/* Association Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Association</label>
            <select
              value={selectedAssociation}
              onChange={(e) => setSelectedAssociation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Associations</option>
              {associations?.map(assoc => (
                <option key={assoc.id} value={assoc.id}>
                  {assoc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Management Company Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Management Co.</label>
            <select
              value={selectedManagementCompany}
              onChange={(e) => setSelectedManagementCompany(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Companies</option>
              {managementCompanies?.map(mc => (
                <option key={mc.id} value={mc.id}>
                  {mc.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Filter</label>
            <div className="flex items-center h-10">
              {discrepancyFilter !== 'all' && (
                <div className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  <span className="font-medium">
                    {discrepancyFilter === 'issues' && 'Issues Found'}
                    {discrepancyFilter === 'mismatches' && 'Amount Mismatches'}
                    {discrepancyFilter === 'unmatched' && 'Unmatched Transactions'}
                    {discrepancyFilter === 'matched' && 'Matched & Correct'}
                  </span>
                  <button
                    onClick={() => setDiscrepancyFilter('all')}
                    className="hover:bg-blue-200 rounded-full p-1"
                    title="Clear filter"
                  >
                    ✕
                  </button>
                </div>
              )}
              {discrepancyFilter === 'all' && (
                <span className="text-sm text-gray-500">No filter applied - Click metrics above to filter</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          Error loading transactions: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Transactions Table */}
      {!isLoading && !error && transactions && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  ID
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Type
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{maxWidth: '150px'}}>
                  From
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{maxWidth: '150px'}}>
                  To
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Amount
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Expected
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Status
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Verified
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Date
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((txn) => {
                const hasDiff = hasDiscrepancy(txn);
                const isUnmatched = !txn.expected_amount && txn.transaction_type === 'order_payment';
                // For unmatched transactions, variance is the full amount (since expected is 0)
                const variance = txn.expected_amount ? txn.amount - txn.expected_amount : (isUnmatched ? txn.amount : 0);

                // Different colors for different issues
                let rowClass = 'hover:bg-gray-50';
                if (hasDiff) {
                  rowClass += ' bg-red-50 border-l-4 border-red-500'; // Amount mismatch - red
                } else if (isUnmatched) {
                  rowClass += ' bg-yellow-50 border-l-4 border-yellow-500'; // Unmatched - yellow
                }

                const isExpanded = expandedRows.has(txn.id);
                const children = childrenCache.get(txn.id) || [];

                // Debug logging for transaction 4048
                if (txn.id === 4048 && children.length > 0) {
                  console.log(`[Render] Transaction 4048 children from cache:`, children.map(c => ({ id: c.id, parent_transaction_id: c.parent_transaction_id })));
                  console.log(`[Render] Full children array for 4048:`, children);
                }

                return (
                  <React.Fragment key={txn.id}>
                    <tr className={rowClass}>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-1">
                          {/* Chevron button to expand/collapse children */}
                          <button
                            onClick={() => toggleRowExpansion(txn.id)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title={isExpanded ? "Collapse children" : "Expand children"}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleViewTransaction(txn)}
                            className="text-blue-600 hover:text-blue-900 underline"
                            title="Click to view details"
                          >
                            #{txn.id}
                          </button>
                        </div>
                      </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      <span className="capitalize">{txn.transaction_type?.replace(/_/g, ' ') || 'N/A'}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 truncate" style={{maxWidth: '150px'}} title={formatAccountName(txn.account_from)}>
                      {formatAccountName(txn.account_from)}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 truncate" style={{maxWidth: '150px'}} title={formatAccountName(txn.account_to)}>
                      {formatAccountName(txn.account_to)}
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs font-semibold ${hasDiff ? 'text-red-700' : 'text-gray-900'}`}>
                      {formatCurrency(txn.amount)}
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs ${hasDiff ? 'text-red-700 font-semibold' : isUnmatched ? 'text-yellow-700' : 'text-gray-900'}`}>
                      {txn.expected_amount !== null && txn.expected_amount !== undefined ? (
                        <div className="flex flex-col">
                          <span>{formatCurrency(txn.expected_amount)}</span>
                          {Math.abs(variance) >= 0.01 && (
                            <span className="text-red-700 font-semibold text-xs">
                              ({variance >= 0 ? '+' : ''}{formatCurrency(variance)})
                            </span>
                          )}
                        </div>
                      ) : isUnmatched ? (
                        <div className="flex flex-col">
                          <span className="text-yellow-700 font-medium italic text-xs">$0.00</span>
                          <span className="text-yellow-700 font-semibold text-xs">
                            ({variance >= 0 ? '+' : ''}{formatCurrency(variance)})
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      {txn.status === 'executed' ? (
                        <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Executed">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : txn.status === 'pending' ? (
                        <svg className="w-5 h-5 text-yellow-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Pending">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      ) : txn.status === 'in_progress' ? (
                        <svg className="w-5 h-5 text-blue-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="In Progress">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                        </svg>
                      ) : txn.status === 'cancelled' ? (
                        <svg className="w-5 h-5 text-gray-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Cancelled">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : txn.status === 'failed' ? (
                        <svg className="w-5 h-5 text-red-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Failed">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(txn.status)}`}>
                          {txn.status}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      {txn.verified ? (
                        <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Verified">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-amber-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Not verified">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(txn.transaction_date)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-medium">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleViewTransaction(txn)}
                          className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                          title="View details"
                        >
                          View
                        </button>
                        <button
                          onClick={() => {
                            setCreateParentId(txn.id);
                            setShowCreateModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 px-2 py-1 border border-purple-300 rounded hover:bg-purple-50"
                          title="Add child transaction"
                        >
                          +
                        </button>
                        {!txn.verified && (
                          <button
                            onClick={() => {
                              if (window.confirm('Verify this transaction?')) {
                                verifyMutation.mutate(txn.id);
                              }
                            }}
                            disabled={verifyMutation.isPending}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50"
                            title="Verify transaction"
                          >
                            ✓
                          </button>
                        )}
                        {/* Execute button removed - transactions auto-execute when balance is released */}
                      </div>
                    </td>
                  </tr>
                  {/* Loading skeleton for children */}
                  {isExpanded && loadingRows.has(txn.id) && (
                    <>
                      {[1, 2, 3].map((i) => (
                        <tr key={`skeleton-${txn.id}-${i}`} className="bg-gray-50 border-l-4 border-gray-300 animate-pulse">
                          <td className="px-3 py-3 whitespace-nowrap text-sm" style={{ paddingLeft: '24px' }}>
                            <div className="flex items-center gap-1">
                              <div className="w-6 h-6 bg-gray-200 rounded" />
                              <div className="h-4 w-16 bg-gray-200 rounded" />
                            </div>
                          </td>
                          <td className="px-3 py-3"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3 text-center"><div className="w-5 h-5 bg-gray-200 rounded-full mx-auto" /></td>
                          <td className="px-3 py-3 text-center"><div className="w-5 h-5 bg-gray-200 rounded-full mx-auto" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
                          <td className="px-3 py-3"><div className="h-6 w-24 bg-gray-200 rounded" /></td>
                        </tr>
                      ))}
                    </>
                  )}
                  {/* Children rows - recursive */}
                  {isExpanded && !loadingRows.has(txn.id) && children.length > 0 && (() => {
                    // Recursive function to render children at any depth
                    const renderChildRow = (child: any, depth: number = 1, visited: Set<number> = new Set()): JSX.Element[] => {
                      // Prevent infinite loops - don't render if already in the path
                      if (visited.has(child.id)) {
                        console.warn(`Circular reference detected: Transaction ${child.id} already in path`, Array.from(visited));
                        return [];
                      }

                      // Max depth protection
                      if (depth > 10) {
                        console.warn(`Maximum depth (10) exceeded for transaction ${child.id}`);
                        return [];
                      }

                      // Add current child to visited set
                      const newVisited = new Set(visited).add(child.id);

                      const childChildren = childrenCache.get(child.id) || [];
                      const hasChildren = childChildren.length > 0;
                      const isChildExpanded = expandedRows.has(child.id);

                      // Check if child has variance
                      const childHasVariance = child.expected_amount && Math.abs(child.amount - child.expected_amount) >= 0.01;

                      return [
                        <tr key={`child-${child.id}`} className={`${childHasVariance ? 'bg-red-50' : 'bg-gray-50'} border-l-4 ${childHasVariance ? 'border-red-500' : depth === 1 ? 'border-gray-300' : 'border-blue-300'}`}>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium" style={{ paddingLeft: `${24 + (depth - 1) * 32}px` }}>
                            <div className="flex items-center gap-1">
                              {hasChildren ? (
                                <button
                                  onClick={() => toggleRowExpansion(child.id)}
                                  className="text-gray-500 hover:text-gray-700 p-1"
                                >
                                  <svg className={`w-4 h-4 transition-transform ${isChildExpanded ? 'transform rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              ) : (
                                <div className="w-6 h-6" />
                              )}
                              <button
                                onClick={() => handleViewTransaction(child)}
                                className="text-blue-600 hover:text-blue-900 underline text-xs"
                                title="Click to view child details"
                              >
                                {'↳'.repeat(depth)} #{child.id}
                              </button>
                            </div>
                          </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                        <span className="capitalize">{child.transaction_type?.replace(/_/g, ' ') || 'N/A'}</span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-900 truncate" style={{maxWidth: '150px'}} title={formatAccountName(child.account_from)}>
                        {formatAccountName(child.account_from)}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-900 truncate" style={{maxWidth: '150px'}} title={formatAccountName(child.account_to)}>
                        {formatAccountName(child.account_to)}
                      </td>
                      <td className={`px-3 py-3 whitespace-nowrap text-xs ${childHasVariance ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>
                        {formatCurrency(child.amount)}
                      </td>
                      <td className={`px-3 py-3 whitespace-nowrap text-xs ${childHasVariance ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>
                        {child.expected_amount !== null && child.expected_amount !== undefined ? (
                          <div className="flex flex-col">
                            <span>{formatCurrency(child.expected_amount)}</span>
                            {Math.abs(child.amount - child.expected_amount) >= 0.01 && (
                              <span className="text-red-700 font-semibold text-xs">
                                ({(child.amount - child.expected_amount) >= 0 ? '+' : ''}{formatCurrency(child.amount - child.expected_amount)})
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {child.status === 'executed' ? (
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Executed">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : child.status === 'pending' ? (
                          <svg className="w-5 h-5 text-yellow-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Pending">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                        ) : child.status === 'cancelled' ? (
                          <svg className="w-5 h-5 text-gray-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Cancelled">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        ) : child.status === 'failed' ? (
                          <svg className="w-5 h-5 text-red-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Failed">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(child.status)}`}>
                            {child.status}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {child.verified ? (
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Verified">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-amber-500 mx-auto" fill="currentColor" viewBox="0 0 20 20" title="Not verified">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                        {formatDate(child.transaction_date)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs font-medium">
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleViewTransaction(child)}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 text-xs"
                            title="View details"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setCreateParentId(child.id);
                              setShowCreateModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 px-2 py-1 border border-purple-300 rounded hover:bg-purple-50 text-xs"
                            title="Add child transaction"
                          >
                            +
                          </button>
                          {!child.verified && (
                            <button
                              onClick={() => {
                                if (window.confirm('Verify this child transaction?')) {
                                  verifyMutation.mutate(child.id);
                                }
                              }}
                              disabled={verifyMutation.isPending}
                              className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 text-xs"
                              title="Verify transaction"
                            >
                              ✓
                            </button>
                          )}
                          {/* Execute button removed - transactions auto-execute when balance is released */}
                        </div>
                      </td>
                    </tr>,
                    // Recursively render this child's children if expanded
                    ...(isChildExpanded && hasChildren ?
                      // Filter to only get DIRECT children (parent_transaction_id === child.id)
                      childChildren
                        .filter((gc: any) => gc.parent_transaction_id === child.id)
                        .flatMap(grandchild => renderChildRow(grandchild, depth + 1, newVisited))
                      : [])
                      ];
                    };

                    // Filter to get only direct children (parent_transaction_id === txn.id)
                    const directChildren = children.filter(c => c.parent_transaction_id === txn.id);
                    const parentVisited = new Set<number>([txn.id]); // Start with parent in visited set
                    return directChildren.flatMap(child => renderChildRow(child, 1, parentVisited));
                  })()}
                  {isExpanded && children.length === 0 && (
                    <tr className="bg-gray-50">
                      <td colSpan={10} className="px-3 py-2 text-xs text-gray-500 italic text-center">
                        No child transactions
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && transactions && transactions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {discrepancyFilter === 'issues' && 'No issues found - all transactions match!'}
            {discrepancyFilter === 'mismatches' && 'No amount mismatches found!'}
            {discrepancyFilter === 'unmatched' && 'No unmatched transactions found!'}
            {discrepancyFilter === 'matched' && 'No matched transactions found.'}
            {discrepancyFilter === 'all' && 'No transactions found'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && filteredTransactions && filteredTransactions.length > 0 && (
        <div className="mt-6 bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + allTransactions.length, totalTransactions)} of {totalTransactions} transactions
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-1 rounded-md text-sm ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedTransaction(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Transaction #{selectedTransaction.id}</h2>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Alert Banners */}
              <div>
                {hasDiscrepancy(selectedTransaction) && (
                  <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 font-semibold mb-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Amount Discrepancy Detected
                    </div>
                    <p className="text-sm text-red-700">
                      Stripe charged{' '}
                      <span className="font-bold">{formatCurrency(selectedTransaction.amount)}</span>
                      {' '}but order total was{' '}
                      <span className="font-bold">{formatCurrency(selectedTransaction.expected_amount || 0)}</span>
                      {' '}(variance: {formatCurrency((selectedTransaction.amount - (selectedTransaction.expected_amount || 0)))})
                    </p>
                  </div>
                )}
                {!selectedTransaction.expected_amount && selectedTransaction.transaction_type === 'order_payment' && (
                  <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-800 font-semibold mb-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Unmatched Transaction
                    </div>
                    <p className="text-sm text-yellow-700">
                      This Stripe transaction could not be matched to any order in the system.
                    </p>
                  </div>
                )}
              </div>

              {/* Parent Transaction Details */}
              <div>
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Transaction Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <p className="font-medium capitalize">
                      {selectedTransaction.transaction_type?.replace(/_/g, ' ') || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <p className="font-medium">{selectedTransaction.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Verified:</span>
                    <p className="font-medium">
                      {selectedTransaction.verified ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Yes
                          {selectedTransaction.verified_at && ` (${formatDateTime(selectedTransaction.verified_at)})`}
                        </span>
                      ) : (
                        <span className="text-amber-600 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          No
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Parent Transaction:</span>
                    <p className="font-medium">
                      {(selectedTransaction.parent_transaction || selectedTransaction.parent_transaction_id) ? (
                        <>
                          <button
                            onClick={async () => {
                              const parentId = selectedTransaction.parent_transaction?.id || selectedTransaction.parent_transaction_id;
                              if (parentId) {
                                const parent = await financialTransactionApi.getTransaction(parentId, true, false);
                                setSelectedTransaction(parent);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 underline"
                          >
                            #{selectedTransaction.parent_transaction?.id || selectedTransaction.parent_transaction_id}
                          </button>
                          {selectedTransaction.parent_transaction?.transaction_type && (
                            <span> ({selectedTransaction.parent_transaction.transaction_type.replace(/_/g, ' ')})</span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Provider:</span>
                    <p className="font-medium">{selectedTransaction.provider}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">From Account:</span>
                    <p className="font-medium">
                      {selectedTransaction.account_from ?
                        formatAccountName(selectedTransaction.account_from) :
                        (selectedTransaction.account_from_id ? `Account #${selectedTransaction.account_from_id}` : '-')
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">To Account:</span>
                    <p className="font-medium">
                      {selectedTransaction.account_to ?
                        formatAccountName(selectedTransaction.account_to) :
                        (selectedTransaction.account_to_id ? `Account #${selectedTransaction.account_to_id}` : '-')
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Amount (Stripe):</span>
                    <p className={`font-medium ${hasDiscrepancy(selectedTransaction) ? 'text-red-700 font-bold' : ''}`}>
                      {formatCurrency(selectedTransaction.amount)}
                    </p>
                  </div>
                  {selectedTransaction.expected_amount && (
                    <div>
                      <span className="text-gray-600">Expected Amount (Order):</span>
                      <p className={`font-medium ${hasDiscrepancy(selectedTransaction) ? 'text-red-700 font-bold' : ''}`}>
                        {formatCurrency(selectedTransaction.expected_amount)}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Date & Time:</span>
                    <p className="font-medium">{formatDateTime(selectedTransaction.transaction_date)}</p>
                  </div>
                </div>
              </div>

              {/* Source and Destination Details */}
              {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Source Details (Stripe) */}
                  <div>
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Source Details (Stripe)
                    </h3>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="space-y-3 text-sm">
                      {selectedTransaction.metadata.stripe_source && (
                        <div>
                          <span className="text-gray-600 font-medium">Stripe Charge ID:</span>
                          <a
                            href={`https://dashboard.stripe.com/payments/${selectedTransaction.metadata.stripe_source}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs mt-1 text-blue-600 hover:text-blue-800 underline break-all flex items-center gap-1"
                          >
                            {selectedTransaction.metadata.stripe_source}
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                            </svg>
                          </a>
                        </div>
                      )}
                      {selectedTransaction.metadata.checkout_session_id && (
                        <div>
                          <span className="text-gray-600 font-medium">Checkout Session:</span>
                          <p className="font-mono text-xs mt-1 text-blue-600 break-all">
                            {selectedTransaction.metadata.checkout_session_id}
                          </p>
                        </div>
                      )}
                      {selectedTransaction.metadata.order_id && (
                        <div>
                          <span className="text-gray-600 font-medium">Order ID:</span>
                          <a
                            href={`/backoffice/orders/${selectedTransaction.metadata.order_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs mt-1 text-green-600 hover:text-green-800 underline break-all flex items-center gap-1"
                          >
                            #{selectedTransaction.metadata.order_id}
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                            </svg>
                          </a>
                        </div>
                      )}
                      {selectedTransaction.metadata.payment_intent && (
                        <div>
                          <span className="text-gray-600 font-medium">Payment Intent:</span>
                          <a
                            href={`https://dashboard.stripe.com/payments/${selectedTransaction.metadata.payment_intent}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs mt-1 text-blue-600 hover:text-blue-800 underline break-all flex items-center gap-1"
                          >
                            {selectedTransaction.metadata.payment_intent}
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                            </svg>
                          </a>
                        </div>
                      )}
                      {selectedTransaction.metadata.customer_email && (
                        <div>
                          <span className="text-gray-600 font-medium">Customer Email:</span>
                          <p className="text-xs mt-1">{selectedTransaction.metadata.customer_email}</p>
                        </div>
                      )}
                      {Object.entries(selectedTransaction.metadata).map(([key, value]) => {
                        // Skip already displayed fields
                        if (['stripe_source', 'checkout_session_id', 'order_id', 'payment_intent', 'customer_email'].includes(key)) {
                          return null;
                        }
                        return (
                          <div key={key}>
                            <span className="text-gray-600 font-medium capitalize">{key.replace(/_/g, ' ')}:</span>
                            <p className="text-xs mt-1 break-all">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                  {/* Destination Details (Order Payment) */}
                  <div>
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Destination Details (Order Payment)
                    </h3>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="text-gray-600 font-medium">Transaction Type:</span>
                          <p className="text-xs mt-1 capitalize font-semibold text-green-700">
                            {selectedTransaction.transaction_type?.replace(/_/g, ' ') || 'N/A'}
                          </p>
                        </div>
                        {selectedTransaction.reference_type === 'order' && selectedTransaction.reference_id && (
                          <div>
                            <span className="text-gray-600 font-medium">Linked Order:</span>
                            <a
                              href={`/backoffice/orders/${selectedTransaction.reference_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs mt-1 text-green-600 hover:text-green-800 underline font-semibold break-all flex items-center gap-1"
                            >
                              Order #{selectedTransaction.reference_id}
                              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                              </svg>
                            </a>
                          </div>
                        )}
                        {selectedTransaction.expected_amount && (
                          <div>
                            <span className="text-gray-600 font-medium">Expected Amount:</span>
                            <p className="text-xs mt-1 font-semibold text-green-700">
                              {formatCurrency(selectedTransaction.expected_amount)}
                            </p>
                          </div>
                        )}
                        {selectedTransaction.amount && (
                          <div>
                            <span className="text-gray-600 font-medium">Actual Amount Received:</span>
                            <p className="text-xs mt-1 font-semibold text-green-700">
                              {formatCurrency(selectedTransaction.amount)}
                            </p>
                          </div>
                        )}
                        {selectedTransaction.expected_amount && selectedTransaction.amount && (
                          <div>
                            <span className="text-gray-600 font-medium">Variance:</span>
                            <p className={`text-xs mt-1 font-semibold ${
                              Math.abs(selectedTransaction.amount - selectedTransaction.expected_amount) <= 0.01
                                ? 'text-green-700'
                                : 'text-red-700'
                            }`}>
                              {Math.abs(selectedTransaction.amount - selectedTransaction.expected_amount) <= 0.01
                                ? 'Match ✓'
                                : `${formatCurrency(Math.abs(selectedTransaction.amount - selectedTransaction.expected_amount))} difference`
                              }
                            </p>
                          </div>
                        )}
                        {selectedTransaction.description && selectedTransaction.description !== 'null' && (
                          <div>
                            <span className="text-gray-600 font-medium">Description:</span>
                            <p className="text-xs mt-1">{selectedTransaction.description}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600 font-medium">Status:</span>
                          <p className="text-xs mt-1">
                            <span className={`inline-block px-2 py-1 rounded ${getStatusColor(selectedTransaction.status)}`}>
                              {selectedTransaction.status}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Child Transactions */}
              {selectedTransaction.children && selectedTransaction.children.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Transaction movements
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full">
                        {selectedTransaction.children.length} {selectedTransaction.children.length === 1 ? 'transaction' : 'transactions'}
                      </span>
                    </h3>
                    <button
                      onClick={() => {
                        setCreateParentId(selectedTransaction.id);
                        setShowCreateModal(true);
                      }}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Child
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      // Build recursive tree structure from flat children list
                      const buildTree = (children: any[], parentId: number | null = selectedTransaction.id): any[] => {
                        return children
                          .filter(child => child.parent_transaction_id === parentId)
                          .map(child => ({
                            ...child,
                            children: buildTree(children, child.id)
                          }));
                      };

                      // Recursive component to render transaction tree
                      const TransactionNode = ({ transaction, level = 0 }: { transaction: any; level?: number }) => (
                        <div key={transaction.id} className={`${level > 0 ? 'ml-6 mt-2' : ''}`}>
                          <div className={`bg-gray-50 p-3 rounded border ${level === 0 ? 'border-gray-200' : 'border-l-4 border-blue-300 border-t border-r border-b border-gray-200'}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-700 mb-1">
                                  {level > 0 && <span className="text-blue-500 mr-1">↳</span>}
                                  Transaction #{transaction.id}
                                  {transaction.metadata?.step && (
                                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {formatStepLabel(transaction.metadata.step)}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium">{formatAccountName(transaction.account_from)}</span>
                                  <span className="mx-2">→</span>
                                  <span className="font-medium">{formatAccountName(transaction.account_to)}</span>
                                </div>
                                {transaction.metadata?.flow_type && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {transaction.metadata.flow_type.replace(/_/g, ' ')}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-gray-900">{formatCurrency(transaction.amount)}</div>
                                <div className="text-xs text-gray-500 capitalize">{transaction.transaction_type?.replace(/_/g, ' ') || 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                          {/* Render nested children */}
                          {transaction.children && transaction.children.length > 0 && (
                            <div className="mt-1">
                              {transaction.children.map((child: any) => (
                                <TransactionNode key={child.id} transaction={child} level={level + 1} />
                              ))}
                            </div>
                          )}
                        </div>
                      );

                      const tree = buildTree(selectedTransaction.children);
                      return tree.map(node => <TransactionNode key={node.id} transaction={node} />);
                    })()}
                  </div>
                </div>
              )}

              {/* Transaction Actions */}
              {(selectedTransaction.status === 'pending' || !selectedTransaction.verified) && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Transaction Actions</h3>

                  <div className="flex flex-wrap gap-3 mb-4">
                    {/* Verify button - show if not yet verified */}
                    {!selectedTransaction.verified && (
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to verify this transaction?')) {
                            verifyMutation.mutate(selectedTransaction.id);
                          }
                        }}
                        disabled={verifyMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {verifyMutation.isPending ? 'Verifying...' : 'Verify Transaction'}
                      </button>
                    )}

                    {/* Execute button - show if verified and pending */}
                    {/* Execute button removed - transactions auto-execute when balance is released */}

                    {/* Cancel button - always available for pending transactions */}
                    {selectedTransaction.status === 'pending' && (
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to cancel this transaction?')) {
                            cancelMutation.mutate(selectedTransaction.id);
                          }
                        }}
                        disabled={cancelMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                      >
                        {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Transaction'}
                      </button>
                    )}
                  </div>

                  {/* Help text */}
                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                    {!selectedTransaction.verified && (
                      <p><strong>Next step:</strong> Verify this transaction to confirm it is legitimate and accurate.</p>
                    )}
                    {selectedTransaction.verified && selectedTransaction.status === 'pending' && (
                      <p><strong>Status:</strong> Transaction verified. Funds will be available for release from the account balance.</p>
                    )}
                    {selectedTransaction.verified && selectedTransaction.status === 'executed' && (
                      <p className="text-green-700"><strong>Status:</strong> This transaction has been verified and executed.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Transaction Modal */}
      <CreateTransactionDialog
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        parentId={createParentId}
        filteredAccounts={filteredAccounts}
        selectedRole={selectedRole}
        createMutation={createMutation}
      />
      </>
      )}

      {/* Upload Wire Transfer Results Dialog */}
      {showUploadDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowUploadDialog(false);
            setUploadFile(null);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Upload Wire Transfer Results</h2>
              <button
                onClick={() => {
                  setShowUploadDialog(false);
                  setUploadFile(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Instructions</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Upload the wire transfer results file from your bank (CSV or Excel)</li>
                  <li>The file should contain transaction references and actual amounts</li>
                  <li>System will match references to pending withdrawals</li>
                  <li>Transactions will be marked as executed</li>
                  <li>Email notifications will be sent to affected parties</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                {uploadFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {uploadFile.name}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowUploadDialog(false);
                    setUploadFile(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadWireResults}
                  disabled={isUploading || !uploadFile}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                >
                  {isUploading ? 'Uploading...' : 'Upload & Process'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Transactions to Mark as Paid Dialog */}
      {showImportDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowImportDialog(false);
            setImportFile(null);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Mark Transactions as Already Paid</h2>
              <button
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">Instructions</h3>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  <li>Upload a CSV file containing transaction IDs to mark as paid</li>
                  <li>The CSV must have an "id" column with transaction IDs</li>
                  <li>Only parent transactions will be marked (not children)</li>
                  <li>Marked transactions will NOT affect account balances</li>
                  <li>Useful for importing historical transactions</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-teal-50 file:text-teal-700
                    hover:file:bg-teal-100"
                />
                {importFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {importFile.name}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportFile}
                  disabled={isImporting || !importFile}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isImporting ? 'Importing...' : 'Import & Mark Paid'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Release Funds Dialog */}
      {showReleaseFundsDialog && selectedAccountForRelease && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowReleaseFundsDialog(false);
            setReleaseAmount('');
            setSelectedAccountForRelease(null);
            setSelectedBatchId(null);
            setNewBatchName('');
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Release Funds</h2>
              <button
                onClick={() => {
                  setShowReleaseFundsDialog(false);
                  setReleaseAmount('');
                  setSelectedAccountForRelease(null);
                  setSelectedBatchId(null);
                  setNewBatchName('');
                  releaseFundsMutation.reset();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Account: <span className="font-medium">{selectedAccountForRelease.display_name || selectedAccountForRelease.reference || 'Unknown'}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Available Balance:{' '}
                  <span className="font-medium text-green-600">
                    ${(selectedAccountForRelease.balances?.find((b: any) => b.balance_type === 'available')?.amount || 0).toFixed(2)}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Release
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedAccountForRelease.balances?.find((b: any) => b.balance_type === 'available')?.amount || 0}
                  value={releaseAmount}
                  onChange={(e) => setReleaseAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add to Batch
                </label>
                <select
                  value={selectedBatchId === null ? '' : selectedBatchId}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'new') {
                      setSelectedBatchId('new');
                    } else if (value === '') {
                      setSelectedBatchId(null);
                    } else {
                      setSelectedBatchId(parseInt(value));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Auto-select (today's batch)</option>
                  <option value="new">+ Create New Batch</option>
                  {availableBatchesForRelease && availableBatchesForRelease.length > 0 && (
                    <>
                      <option disabled>──────────────</option>
                      {availableBatchesForRelease.map((batch: any) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.batch_number} - ${Number(batch.total_amount).toFixed(2)} ({batch.total_payouts} payouts)
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select an existing batch or create a new one
                </p>
              </div>

              {selectedBatchId === 'new' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Batch Name
                  </label>
                  <input
                    type="text"
                    value={newBatchName}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    placeholder="e.g., January 2026 Payouts"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {releaseFundsMutation.isError && (
                <div className="bg-red-50 border border-red-200 p-4 rounded">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-red-600 text-xl">⚠️</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900 mb-1">
                        {(releaseFundsMutation.error as any)?.response?.error === 'No active payout account configured'
                          ? 'Payout Account Required'
                          : (releaseFundsMutation.error as any)?.response?.error === 'Incomplete payout account configuration'
                          ? 'Payout Account Incomplete'
                          : 'Error Releasing Funds'}
                      </h4>
                      <p className="text-sm text-red-800 mb-2">
                        {(releaseFundsMutation.error as any)?.response?.message || (releaseFundsMutation.error as any)?.message || 'An error occurred'}
                      </p>
                      {(releaseFundsMutation.error as any)?.response?.missing_fields && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-red-900 mb-1">Missing fields:</p>
                          <ul className="list-disc list-inside text-sm text-red-800">
                            {(releaseFundsMutation.error as any).response.missing_fields.map((field: string) => (
                              <li key={field}>{field}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3 p-3 bg-white rounded border border-red-200">
                        <p className="text-sm font-medium text-gray-900 mb-2">To configure payout account:</p>
                        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                          {selectedAccountForRelease?.account_type === 'management_company' && selectedAccountForRelease?.reference ? (
                            <>
                              <li>Click the button below to go to the Management Company edit page</li>
                              <li>Navigate to the <strong>Finance</strong> tab</li>
                              <li>Fill in all required bank account and address fields</li>
                            </>
                          ) : selectedAccountForRelease?.account_type === 'association' && selectedAccountForRelease?.reference ? (
                            <>
                              <li>Click the button below to go to the Association edit page</li>
                              <li>Navigate to the <strong>Finance</strong> tab</li>
                              <li>Fill in all required bank account and address fields</li>
                            </>
                          ) : (
                            <>
                              <li>Go to <strong>Settings → Finance</strong> page</li>
                              <li>Find the account: <strong>{selectedAccountForRelease?.display_name || selectedAccountForRelease?.reference || 'this account'}</strong></li>
                              <li>Click edit and configure payout account details</li>
                            </>
                          )}
                        </ol>
                        {selectedAccountForRelease?.account_type === 'management_company' && selectedAccountForRelease?.reference ? (
                          <a
                            href={`/backoffice/management-companies/${selectedAccountForRelease.reference}/edit`}
                            className="mt-3 inline-block text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                          >
                            → Go to Management Company: {selectedAccountForRelease.display_name || selectedAccountForRelease.reference}
                          </a>
                        ) : selectedAccountForRelease?.account_type === 'association' && selectedAccountForRelease?.reference ? (
                          <a
                            href={`/backoffice/associations/${selectedAccountForRelease.reference}/edit`}
                            className="mt-3 inline-block text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                          >
                            → Go to Association: {selectedAccountForRelease.display_name || selectedAccountForRelease.reference}
                          </a>
                        ) : (
                          <a
                            href="/backoffice/settings"
                            className="mt-3 inline-block text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                          >
                            → Go to Settings page
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded text-sm text-gray-700">
                <p>
                  Releasing funds will move the specified amount from{' '}
                  <strong>available</strong> to <strong>pending_release</strong> and add it to the selected batch.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowReleaseFundsDialog(false);
                    setReleaseAmount('');
                    setSelectedAccountForRelease(null);
                    setSelectedBatchId(null);
                    setNewBatchName('');
                    releaseFundsMutation.reset();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReleaseFundsSubmit}
                  disabled={releaseFundsMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {releaseFundsMutation.isPending ? 'Releasing...' : 'Release Funds'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statement Export Dialog */}
      {showStatementExportDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowStatementExportDialog(false);
            setSelectedStatementId(null);
            setStatementExportAccountId(null);
            setIncludeEmptyAccounts(true);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Export Statement</h2>
              <button
                onClick={() => {
                  setShowStatementExportDialog(false);
                  setSelectedStatementId(null);
                  setStatementExportAccountId(null);
                  setIncludeEmptyAccounts(true);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              {/* Account info (if exporting for specific account) */}
              {statementExportAccountId && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    Exporting for:{' '}
                    <span className="font-medium">
                      {filteredBalanceAccounts?.find((a) => a.id === statementExportAccountId)?.display_name ||
                        filteredBalanceAccounts?.find((a) => a.id === statementExportAccountId)?.reference ||
                        `Account ${statementExportAccountId}`}
                    </span>
                  </p>
                </div>
              )}

              {/* Account selection (if opened from Export All button) */}
              {statementExportAccountId === null && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account (Optional)
                  </label>
                  <select
                    value={statementExportAccountId || ''}
                    onChange={(e) =>
                      setStatementExportAccountId(e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Accounts</option>
                    {filteredBalanceAccounts?.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.display_name || account.reference || `Account ${account.id}`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Leave empty to export all accounts
                  </p>
                </div>
              )}

              {/* Statement selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statement Period
                </label>
                <select
                  value={selectedStatementId || ''}
                  onChange={(e) =>
                    setSelectedStatementId(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a statement...</option>
                  {statements?.map((statement) => (
                    <option key={statement.id} value={statement.id}>
                      {formatStatementMonth(statement.statement_month)} ({statement.statement_number})
                      {statement.status === 'open' ? ' - Current' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="statementExportFormat"
                      value="csv"
                      checked={statementExportFormat === 'csv'}
                      onChange={() => setStatementExportFormat('csv')}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">CSV</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="statementExportFormat"
                      value="pdf"
                      checked={statementExportFormat === 'pdf'}
                      onChange={() => setStatementExportFormat('pdf')}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">PDF (Bank Statement)</span>
                  </label>
                </div>
              </div>

              {/* Include empty accounts checkbox - only show when exporting all accounts */}
              {statementExportAccountId === null && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeEmptyAccounts}
                      onChange={(e) => setIncludeEmptyAccounts(e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Include accounts with no transactions</span>
                  </label>
                </div>
              )}

              {/* Include Platform checkbox - only show when exporting all accounts */}
              {statementExportAccountId === null && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includePlatform}
                      onChange={(e) => setIncludePlatform(e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Include Platform account</span>
                  </label>
                </div>
              )}

              {/* Info box */}
              <div className="bg-blue-50 p-3 rounded text-sm text-gray-700">
                <p>
                  {statementExportFormat === 'csv' ? (
                    <>
                      The CSV export includes starting balance, all transactions (parent and
                      children), and ending balance for each account.
                    </>
                  ) : (
                    <>
                      The PDF export generates a professional bank-style statement with logo,
                      statement period, balance summary, and transaction details.
                      {!includeEmptyAccounts && statementExportAccountId === null && (
                        <> Accounts with no transactions will be excluded.</>
                      )}
                    </>
                  )}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowStatementExportDialog(false);
                    setSelectedStatementId(null);
                    setStatementExportAccountId(null);
                    setIncludeEmptyAccounts(true);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatementExport}
                  disabled={!selectedStatementId || isExportingStatement}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isExportingStatement ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Batches Dialog */}
      {showBatchesDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowBatchesDialog(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Withdrawal Batches</h2>
              <button
                onClick={() => setShowBatchesDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!withdrawalBatches ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading batches...</p>
              </div>
            ) : withdrawalBatches.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No withdrawal batches found.
              </div>
            ) : (
              <div className="space-y-4">
                {withdrawalBatches.map((batch: any) => (
                  <div key={batch.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{batch.batch_number}</h3>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(batch.created_at).toLocaleDateString()} {new Date(batch.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          batch.status === 'completed' ? 'bg-green-100 text-green-800' :
                          batch.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          batch.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {batch.status}
                        </span>
                        {/* Upload Results Button - show for downloaded/submitted batches */}
                        {['downloaded', 'submitted', 'draft', 'generated'].includes(batch.status) && (
                          <button
                            onClick={() => {
                              setUploadingBatchId(batch.id);
                              batchFileInputRef.current?.click();
                            }}
                            disabled={uploadingBatchId === batch.id}
                            className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                            title="Upload Wire Transfer Results"
                          >
                            {uploadingBatchId === batch.id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              // Fetch full transaction details with payout account info
                              // Withdrawals use account_from_id (money leaves the entity account)
                              const { data: batchDetails, error } = await supabase
                                .from('financial_batch_payouts')
                                .select(`
                                  payout_reference,
                                  payout_transaction_id,
                                  financial_transactions!inner (
                                    id,
                                    amount,
                                    currency,
                                    account_from_id,
                                    financial_accounts!financial_transactions_account_from_id_fkey (
                                      id,
                                      account_type,
                                      reference
                                    )
                                  )
                                `)
                                .eq('batch_file_id', batch.id);

                              if (error) throw error;

                              // Fetch payout accounts for all source accounts (entity accounts sending money)
                              const accountIds = batchDetails?.map((bp: any) =>
                                bp.financial_transactions?.account_from_id
                              ).filter(Boolean) || [];

                              const { data: payoutAccounts } = await supabase
                                .from('payout_accounts')
                                .select('*')
                                .in('account_id', accountIds)
                                .eq('is_default', true);

                              const payoutAccountMap = new Map(
                                payoutAccounts?.map((pa: any) => [pa.account_id, pa]) || []
                              );

                              // Generate Airwallex format XLSX with proper template format
                              const transfers: AirwallexTransferRow[] = batchDetails?.map((bp: any) => {
                                const txn = bp.financial_transactions;
                                const payoutAccount = payoutAccountMap.get(txn?.account_from_id);

                                return {
                                  transferTo: 'United States of America',
                                  transferMethod: 'ACH',
                                  currencyRecipientGets: 'USD',
                                  currencyYouPay: 'USD',
                                  amount: Number(txn?.amount || 0),
                                  feePaidBy: 'Payer',
                                  accountName: payoutAccount?.account_holder_name || '',
                                  bankAccountType: payoutAccount?.bank_account_type === 'savings' ? 'Savings' : 'Checking',
                                  bankIdentifierType: 'ACH routing number',
                                  bankIdentifierValue: payoutAccount?.bank_routing_number || '',
                                  accountNumber: payoutAccount?.bank_account_number || '',
                                  transferPurpose: 'Professional / Business Services',
                                  reference: bp.payout_reference || `TXN-${txn?.id}`,
                                  description: payoutAccount?.account_holder_name || '',
                                  recipientType: 'Business',
                                  countryRegion: 'United States of America',
                                  streetAddress: payoutAccount?.street_address || '',
                                  city: payoutAccount?.city || '',
                                  stateProvince: getStateFullName(payoutAccount?.state_province || ''),
                                  postcode: payoutAccount?.postcode || '',
                                };
                              }) || [];

                              // Download XLSX with unique Request IDs per row
                              downloadAirwallexXLSX(transfers, `${batch.batch_number}_airwallex.xlsx`);

                              toast({
                                title: 'Download Complete',
                                description: `Exported ${batchDetails?.length || 0} transactions in Airwallex format`,
                              });
                            } catch (err: any) {
                              toast({
                                title: 'Download Failed',
                                description: err.message,
                                variant: 'destructive',
                              });
                            }
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Download Batch (Airwallex Format)"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Type:</span> {batch.batch_type}
                      </div>
                      <div>
                        <span className="text-gray-500">Total Payouts:</span> {batch.total_payouts}
                      </div>
                      <div>
                        <span className="text-gray-500">Total Amount:</span> ${Number(batch.total_amount).toFixed(2)}
                      </div>
                    </div>
                    {batch.financial_batch_payouts && batch.financial_batch_payouts.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-2">Payouts ({batch.financial_batch_payouts.length}):</p>
                        <div className="space-y-1">
                          {batch.financial_batch_payouts.map((payout: any) => (
                            <div key={payout.id} className="text-sm text-gray-600 flex justify-between">
                              <span>{payout.payout_reference}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                payout.status === 'completed' ? 'bg-green-100 text-green-700' :
                                payout.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {payout.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Hidden file input for batch upload */}
            <input
              ref={batchFileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !uploadingBatchId) {
                  setUploadingBatchId(null);
                  return;
                }

                try {
                  // Parse file (CSV or Excel)
                  let headers: string[] = [];
                  let dataRows: string[][] = [];
                  const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

                  if (isExcel) {
                    // Parse Excel file
                    const arrayBuffer = await file.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

                    if (jsonData.length < 2) {
                      throw new Error('File must contain header row and at least one data row');
                    }

                    headers = (jsonData[0] as string[]).map(h => String(h || '').trim().toLowerCase());
                    dataRows = jsonData.slice(1).map(row =>
                      (row as string[]).map(cell => String(cell || '').trim())
                    );
                  } else {
                    // Parse CSV file
                    const text = await file.text();
                    const lines = text.trim().split('\n');

                    if (lines.length < 2) {
                      throw new Error('File must contain header row and at least one data row');
                    }

                    headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
                    dataRows = lines.slice(1).map(line =>
                      line.split(',').map(v => v.trim().replace(/"/g, ''))
                    );
                  }

                  // Parse header to find relevant columns.
                  //
                  // Airwallex's results export ("AWX payments" format) carries TWO
                  // reference-ish columns:
                  //   - "Reference"          -> our own value (what we exported, e.g. TXN-141953)
                  //   - "Transfer reference" -> Airwallex's internal id (e.g. P260527-QPFWX6V)
                  // We match payouts on OUR reference, so prefer "Reference" and treat
                  // "Transfer reference" as the bank reference. We still fall back to
                  // recipient name + amount when neither reference lines up (e.g. a batch
                  // sent to Airwallex with a different reference scheme).
                  const exactIdx = (name: string) => headers.findIndex(h => h === name);

                  // Our reference column (used to match against payout_reference / TXN-<id>)
                  let referenceIdx = exactIdx('reference');
                  if (referenceIdx === -1) referenceIdx = exactIdx('transfer reference');
                  if (referenceIdx === -1) {
                    referenceIdx = headers.findIndex(h =>
                      h.includes('payout') || (h.includes('reference') && !h.includes('request'))
                    );
                  }
                  if (referenceIdx === -1) {
                    referenceIdx = headers.findIndex(h =>
                      h.includes('request id') || h.includes('reference')
                    );
                  }

                  // Bank reference column (Airwallex's internal id). Prefer the dedicated
                  // "transfer reference" column when it is not already the one we match on.
                  let bankRefIdx = headers.findIndex(h =>
                    (h.includes('bank') && h.includes('ref')) ||
                    h.includes('confirmation') ||
                    h.includes('short reference')
                  );
                  if (bankRefIdx === -1) {
                    const transferRefIdx = exactIdx('transfer reference');
                    if (transferRefIdx !== -1 && transferRefIdx !== referenceIdx) {
                      bankRefIdx = transferRefIdx;
                    }
                  }

                  const statusIdx = headers.findIndex(h =>
                    h.includes('status') || h.includes('result')
                  );

                  // Fallback-matching columns: recipient name + amount.
                  const nameIdx = headers.findIndex(h =>
                    h === 'account name' || h === 'business name' || h.includes('account name')
                  );
                  // Amount can live in several columns; try them in order of preference.
                  // (For platform/self transfers Airwallex leaves "Amount recipient receives"
                  // blank and only fills "Amount payer pays".)
                  const amountIdxCandidates = [
                    headers.findIndex(h => h === 'amount recipient receives'),
                    headers.findIndex(h => h === 'amount payer pays'),
                    headers.findIndex(h => h.includes('amount') && h.includes('recipient')),
                    headers.findIndex(h => h.includes('amount') && h.includes('payer')),
                    headers.findIndex(h => h === 'amount' || h.includes('transfer amount')),
                    headers.findIndex(h => h.includes('amount')),
                  ].filter(i => i >= 0);

                  if (referenceIdx === -1 && nameIdx === -1) {
                    throw new Error('Could not find a reference or account name column to match payouts against.');
                  }

                  const parseAmount = (raw: string): number | null => {
                    if (!raw) return null;
                    const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
                    return Number.isFinite(n) ? n : null;
                  };

                  console.log('DEBUG: Column indices found:', { referenceIdx, statusIdx, bankRefIdx, nameIdx, amountIdxCandidates });
                  console.log('DEBUG: Headers:', headers);

                  // Parse data rows
                  const results: Array<{ reference: string; status: string; bankRef?: string; name?: string; amount?: number | null }> = [];
                  for (const values of dataRows) {
                    const reference = referenceIdx >= 0 && values.length > referenceIdx ? values[referenceIdx] : '';
                    const name = nameIdx >= 0 && values.length > nameIdx ? values[nameIdx] : '';
                    // First populated amount column wins.
                    let amount: number | null = null;
                    for (const idx of amountIdxCandidates) {
                      if (values.length > idx) {
                        const parsed = parseAmount(values[idx]);
                        if (parsed != null) { amount = parsed; break; }
                      }
                    }
                    // Skip blank rows (no reference and no name to match on).
                    if (!reference && !name) continue;
                    results.push({
                      reference,
                      status: statusIdx >= 0 && values.length > statusIdx ? values[statusIdx] : 'completed',
                      bankRef: bankRefIdx >= 0 && values.length > bankRefIdx ? values[bankRefIdx] : undefined,
                      name,
                      amount,
                    });
                  }

                  console.log('DEBUG: Parsed results count:', results.length);
                  console.log('DEBUG: First 3 results:', results.slice(0, 3));
                  console.log('DEBUG: uploadingBatchId:', uploadingBatchId);

                  // Load every payout in this batch once, with the recipient name and
                  // amount, so we can match each result row by reference OR by
                  // recipient name + amount when the reference scheme doesn't line up.
                  const { data: batchPayouts, error: batchPayoutsError } = await supabase
                    .from('financial_batch_payouts')
                    .select(`
                      id,
                      payout_transaction_id,
                      payout_reference,
                      financial_transactions!inner (
                        id,
                        amount,
                        account_from_id
                      )
                    `)
                    .eq('batch_file_id', uploadingBatchId);

                  if (batchPayoutsError) throw batchPayoutsError;

                  // Pull a recipient display name per payout from the default payout account
                  // (account_holder_name) so the name fallback can match the file's "Account name".
                  const sourceAccountIds = (batchPayouts || [])
                    .map((bp: any) => bp.financial_transactions?.account_from_id)
                    .filter(Boolean);
                  const { data: holderAccounts } = await supabase
                    .from('payout_accounts')
                    .select('account_id, account_holder_name')
                    .in('account_id', sourceAccountIds)
                    .eq('is_default', true);
                  const holderByAccount = new Map(
                    (holderAccounts || []).map((pa: any) => [pa.account_id, pa.account_holder_name])
                  );

                  const norm = (s: string) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
                  const amountsClose = (a: number, b: number) => Math.abs(a - b) < 0.005;

                  // Pre-index payouts for fast lookup. A payout can be matched by:
                  //   1. exact payout_reference
                  //   2. TXN-<id> embedded in the file reference
                  //   3. recipient name + amount
                  const enriched = (batchPayouts || []).map((bp: any) => {
                    const txn = bp.financial_transactions;
                    return {
                      bp,
                      txnId: txn?.id as number | undefined,
                      amount: Number(txn?.amount ?? NaN),
                      name: norm(holderByAccount.get(txn?.account_from_id) || ''),
                    };
                  });

                  const findPayout = (result: { reference: string; name?: string; amount?: number | null }) => {
                    const ref = String(result.reference || '').trim();
                    // 1. exact payout_reference
                    if (ref) {
                      const exact = enriched.find(e => String(e.bp.payout_reference || '') === ref);
                      if (exact) return exact;
                      // 2. TXN-<id> anywhere in the reference
                      const m = ref.match(/TXN-(\d+)/i);
                      if (m) {
                        const byId = enriched.find(e => e.txnId === Number(m[1]));
                        if (byId) return byId;
                      }
                    }
                    // 3. recipient name + amount
                    if (result.name && result.amount != null && Number.isFinite(result.amount)) {
                      const rn = norm(result.name);
                      const byNameAmt = enriched.find(e =>
                        e.name && e.name === rn && Number.isFinite(e.amount) && amountsClose(e.amount, result.amount as number)
                      );
                      if (byNameAmt) return byNameAmt;
                    }
                    // amount-only as a last resort (unique amount in the batch)
                    if (result.amount != null && Number.isFinite(result.amount)) {
                      const candidates = enriched.filter(e => Number.isFinite(e.amount) && amountsClose(e.amount, result.amount as number));
                      if (candidates.length === 1) return candidates[0];
                    }
                    return null;
                  };

                  // Update batch payouts based on results
                  let successCount = 0;
                  let failCount = 0;
                  const matchedPayoutIds = new Set<number>();

                  for (const result of results) {
                    const match = findPayout(result);

                    if (!match || matchedPayoutIds.has(match.bp.id)) {
                      console.log('DEBUG: No payout matched result', { reference: result.reference, name: result.name, amount: result.amount });
                      continue;
                    }
                    matchedPayoutIds.add(match.bp.id);
                    const payout = match.bp;

                    const isSuccess = !result.status ||
                      result.status.toLowerCase().includes('success') ||
                      result.status.toLowerCase().includes('completed') ||
                      result.status.toLowerCase().includes('paid');

                    // Update batch payout status. Store the bank reference (Airwallex
                    // internal id) when present, else keep our own reference.
                    await supabase
                      .from('financial_batch_payouts')
                      .update({
                        status: isSuccess ? 'completed' : 'failed',
                        payout_reference: result.bankRef || result.reference || payout.payout_reference,
                        completed_at: isSuccess ? new Date().toISOString() : null,
                      })
                      .eq('id', payout.id);

                    // Update transaction status if successful
                    if (isSuccess && payout.payout_transaction_id) {
                      await supabase
                        .from('financial_transactions')
                        .update({
                          status: 'executed',
                          executed_at: new Date().toISOString(),
                        })
                        .eq('id', payout.payout_transaction_id);
                    }

                    if (isSuccess) successCount++;
                    else failCount++;
                  }

                  // Update batch status
                  const allCompleted = failCount === 0 && successCount > 0;
                  await supabase
                    .from('financial_batch_files')
                    .update({
                      status: allCompleted ? 'completed' : 'submitted',
                      completed_at: allCompleted ? new Date().toISOString() : null,
                      updated_at: new Date().toISOString(),
                    })
                    .eq('id', uploadingBatchId);

                  const unmatchedCount = results.length - (successCount + failCount);
                  toast({
                    title: 'Upload Complete',
                    description: `Processed ${results.length} rows: ${successCount} executed, ${failCount} failed` +
                      (unmatchedCount > 0 ? `, ${unmatchedCount} unmatched` : ''),
                  });

                  // Refresh batches
                  refetchBatches();

                } catch (err: any) {
                  toast({
                    title: 'Upload Failed',
                    description: err.message,
                    variant: 'destructive',
                  });
                } finally {
                  setUploadingBatchId(null);
                  e.target.value = '';
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialTransactions;
